import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { loadPincodeDb } from '../utils/pincodeDb';
import {
  User as UserIcon, Phone as PhoneIcon, Mail as MailIcon, MapPin as MapPinIcon, Store as StoreIcon, Package as PackageIcon,
  Truck as TruckIcon, IndianRupee as RupeeIcon, Tag as TagIcon, Box as BoxIcon, Check as CheckIcon, Sparkles as SparklesIcon,
  Search as SearchIcon, RefreshCw as RefreshIcon, LayoutGrid as GridIcon, Plus as PlusIcon, PackageOpen as PackageOpenIcon, ExternalLink as ExternalLinkIcon,
  X as XIcon, ArrowRight, ArrowLeft, FileText as FileTextIcon, Pencil as PencilIcon, Trash2 as TrashIcon,
  MoreVertical as MoreVerticalIcon, Archive as ArchiveIcon, RotateCcw as RestoreIcon, ChevronDown as ChevronDownIcon, Lock as LockIcon,
} from "lucide-react";
import { API } from '../utils/apiUrls';
import { deepTrimStrings } from '../utils/helpers';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletBalance } from '../redux/slices/walletSlice';
import PickupAddressModal, { deletePickupAddress, DeleteConfirmModal } from '../components/PickupAddressModal';
import { useOnboardingGate } from '../components/onboarding/OnboardingGate';
import OrderCreationIssueModal from '../components/orders/OrderCreationIssueModal';
import { interpretOrderCreationError, interpretOrderCreationResponse } from '../utils/orderCreationError';
import { CARD, EMAIL_RE, suggestSkuFromName, titleCaseLoc, displayLocName, locInitials, LABEL, SPRING, inputClass, BTN_PRIMARY, BTN_OUTLINE, BTN_GHOST, numericKeyGuard, numericPasteGuard, fmtInr, initialsOf, formatCarrier, carrierAccent, estDeliveryDate, ASSUMED_TAT_DAYS } from '../components/createorder/tokens';
import { readLayoutPreference, writeLayoutPreference, FormLayoutCard } from '../components/createorder/layoutPreference';
import JourneyGroup, { FieldError } from '../components/createorder/JourneyGroup';
import ShipmentTicket from '../components/createorder/ShipmentTicket';
import BookedSuccess from '../components/createorder/BookedSuccess';
import PackageStudio from '../components/createorder/PackageStudio';
import SmartAllocationEngine from '../components/createorder/SmartAllocationEngine';
import CourierPicker from '../components/createorder/CourierPicker';
import { useStepRowAutoScroll, useMoreBelowCue, useNumberWheelBlur } from '../components/createorder/hooks';

/* ─────────────────────────────────────────────────────────────────────────────
   The idea: the page is a desk. On the left you fill things in, quietly and
   fast. On the right sits the shipment's ticket — a living artifact where the
   route draws itself, a truck travels from origin to destination as the steps
   complete, and the details print line by line. Booking stamps it.

   Tokens follow the shipment-details page exactly: solid #3877FC accent
   (#2A59BD on hover), slate everywhere else, emerald only for done.
──────────────────────────────────────────────────────────────────────────── */
/* One voice for every field message - shown live on blur and after Continue.
   validateStep's own strings still gate the step; these are what the merchant reads. */
const FIELD_MSG = {
  phoneNumber: "Must be 10 digits — drop the +91 or leading 0",
  name: "Add the recipient's name",
  email: "Enter a valid email address",
  pickup: "Choose a pickup location to continue",
  address: "Add the full address — at least 10 characters",
  city: "Add the city",
  state: "Add the state",
  pincode: "Enter the 6-digit pincode",
  clientOrderId: "Add an order reference, or hit Generate",
  invoiceValue: "Enter an invoice value above ₹0",
  codAmount: "Enter the amount to collect on delivery",
  "item-name": "Add the item name",
  "item-skuId": "Add a SKU, or generate one",
  "item-quantity": "Quantity must be a whole number, 1 or more",
  "item-unitPrice": "Add the unit price",
  weight: "Enter a weight above 0, up to 200 kg",
  length: "Enter a size above 0, up to 500 cm",
  breadth: "Enter a size above 0, up to 500 cm",
  height: "Enter a size above 0, up to 500 cm",
};
const FIELD_STEP = { phoneNumber: 1, name: 1, email: 1, address: 1, city: 1, state: 1, pincode: 1, pickup: 2, clientOrderId: 3, invoiceValue: 3, codAmount: 3, weight: 4, length: 4, breadth: 4, height: 4 };

const CreateForwardOrder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [layoutMode, setLayoutMode] = useState(readLayoutPreference); // "horizontal" | "vertical", see LAYOUT_COOKIE
  const [pendingDraftLanding, setPendingDraftLanding] = useState(false); // draft/clone hydrated → place the stepper from its data
  const stacked = layoutMode === "vertical";
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [bookedAt, setBookedAt] = useState(null); // stamped moment of booking (client clock)
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [pincodeLookupFailed, setPincodeLookupFailed] = useState(false); // lookup miss is not an error - the merchant just types city/state
  /* The saved-locations list scrolls inside the card, with edge fades that
     appear only when more content lies beyond the edge. */
  const [pickupMenuFor, setPickupMenuFor] = useState(null); // which card's ... menu is open
  const [refreshingPickups, setRefreshingPickups] = useState(false); // refresh glyph spinning (min one turn)
  const pickupListRef = useRef(null);
  const pickupListRO = useRef(null);
  const stepRowRef = useRef(null); // the horizontally-scrollable step pills row (mobile)
  const stepBodyRef = useRef(null); // the current step's scrolling body — drives the "more below" cue
  const footerRef = useRef(null);   // the sticky action dock (so the cue clears it)
  const { moreBelow, scrollForMore, moreBelowComputeRef } = useMoreBelowCue({ stepBodyRef, footerRef, step });
  useStepRowAutoScroll(stepRowRef, step);
  useNumberWheelBlur();
  const [pickupEdges, setPickupEdges] = useState({ top: true, bottom: true });
  const updatePickupEdges = () => {
    const el = pickupListRef.current;
    if (!el) return;
    const top = el.scrollTop <= 2;
    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setPickupEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    moreBelowComputeRef.current(); // the list's own scroll/resize changes what's hidden below
  };
  const [courierServiceability, setCourierServiceability] = useState([]);
  const [customerLookupResults, setCustomerLookupResults] = useState([])
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const location = useLocation();
  const cloneFrom = location?.state?.cloneFrom;
  const [draftOrderId, setDraftOrderId] = useState(location?.state?.draftOrderId || null);
  const cloneMode = Boolean(cloneFrom);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSuccessModal, setDraftSuccessModal] = useState({ open: false, draftId: null, isUpdate: false });
  const [orderIssue, setOrderIssue] = useState(null);
  const dispatch = useDispatch();
  const walletType = useSelector((s) => s.wallet?.type); // fallback settlement source (redux wallet type)
  // Authoritative settlement type from OMS: resolves SQL wallet type OR Mongo onboarding preference.
  const [resolvedSettlement, setResolvedSettlement] = useState(null);
  const { requireBusinessOps } = useOnboardingGate();

  const [formData, setFormData] = useState({
    customerDetails: {
      name: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
    },
    orderDetails: {
      clientOrderId: "",
      invoiceValue: 0,
      itemDetails: [
        {
          name: "",
          quantity: 1,
          unitPrice: "",
          skuId: "",
          hsnCode: "",
          isFragile: false,
        },
      ]
    },
    packageDetails: {
      weight: "",
      length: "",
      breadth: "",
      height: "",
    },
    courierDetails: {
      courierPartner: "",
      useRecommendation: true,
    },
    paymentMode: "Prepaid",
  });

  const resetForm = () => {
    setFormData({
      customerDetails: {
        name: "",
        phoneNumber: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
      },
      orderDetails: {
        clientOrderId: "",
        invoiceValue: 0,
        itemDetails: [
          {
            name: "",
            quantity: 1,
            unitPrice: "",
            skuId: "",
            hsnCode: "",
            isFragile: false,
          },
        ]
      },
      packageDetails: {
        weight: "",
        length: "",
        breadth: "",
        height: "",
      },
      courierDetails: {
        courierPartner: "",
        useRecommendation: true,
      },
      paymentMode: "Prepaid",
    });

    setStep(1);
    setHighestStepReached(1); // Reset highest step reached
    setResponseData(null);
    setSelectedPickup(null);
    setErrors({});
    setTouched({});
    setExpandedHSNItems({});
  };

  const [pickupDetails, setPickupDetails] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [courierOptions, setCourierOptions] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({}); // fields visited with content - live validation shows for these (or after Continue)

  /* Order-reference precheck: while the merchant types a reference, ask OMS whether one already
     exists for it (read-only) so we can block before they fill and submit the whole form.
     Fails open (endpoint down / not deployed → allow). cloneMode/retry is skipped (that flow
     brings its own "_retry" reference). */
  const [orderRefCheck, setOrderRefCheck] = useState({ ref: "", outcome: "NONE", awb: null });
  const orderRefSeq = useRef(0);
  const orderRefTimer = useRef(null);
  useEffect(() => {
    const ref = String(formData.orderDetails.clientOrderId || "").trim();
    const seq = ++orderRefSeq.current; // invalidate any in-flight response
    if (orderRefTimer.current) clearTimeout(orderRefTimer.current);
    if (cloneMode || !ref) { setOrderRefCheck({ ref: "", outcome: "NONE", awb: null }); return; }
    orderRefTimer.current = setTimeout(() => {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      axios.get(API.orderReferencePrecheck(ref), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
        .then((resp) => {
          if (seq !== orderRefSeq.current) return;
          if (resp.status >= 200 && resp.status < 300 && resp.data && resp.data.exists) {
            setOrderRefCheck({ ref, outcome: String(resp.data.outcome || "UNKNOWN"), awb: resp.data.awbNumber || null });
          } else {
            setOrderRefCheck({ ref, outcome: "NONE", awb: null }); // non-exists or any error → allow
          }
        })
        .catch(() => { if (seq === orderRefSeq.current) setOrderRefCheck({ ref, outcome: "NONE", awb: null }); });
    }, 450);
    return () => { if (orderRefTimer.current) clearTimeout(orderRefTimer.current); };
  }, [formData.orderDetails.clientOrderId, cloneMode]);
  const [units, setUnits] = useState({ weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' });
  const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i;
  const [highestStepReached, setHighestStepReached] = useState(1); // Track highest step user has reached
  const [expandedHSNItems, setExpandedHSNItems] = useState({}); // Track which items have HSN code expanded

  // Address management state
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrModalMode, setAddrModalMode] = useState("add"); // "add" | "edit"
  const [addrModalLocation, setAddrModalLocation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pickupToDelete, setPickupToDelete] = useState(null);
  const [deletingPickup, setDeletingPickup] = useState(false);

  // Presentation-only state: never touches the payload.
  const [pickupQuery, setPickupQuery] = useState("");
  const [showUnserviceable, setShowUnserviceable] = useState(false);
  const [courierSort, setCourierSort] = useState("cheapest"); // "cheapest" | "fastest" — display order only
  const [awbCopied, setAwbCopied] = useState(false);

  // Helper: decode JWT and extract username
  const getUsernameFromToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || '';
    } catch (e) {
      return '';
    }
  };

  // Generate random SKU: first 3 letters of username + '-' + 6-digit random
  const generateRandomSku = () => {
    const username = getUsernameFromToken();
    const prefix = username.slice(0, 3).toUpperCase();
    const randomSix = Math.floor(100000 + Math.random() * 900000);
    return `${prefix}-${randomSix}`;
  };

  const generateSkuForItem = (index) => {
    handleItemChange(index, 'skuId', generateRandomSku());
  };

  // ---Gen-ID helper ---
  const handleGenerateOrderId = () => {
    setFormData((f) => ({
      ...f,
      orderDetails: {
        ...f.orderDetails,
        clientOrderId: uuidv4(),
      },
    }));
  };

  // --- fetchSKUDetails populates name/price/quantity + package dims ---
  /* SKU typeahead: a debounced catalog search under the SKU field, plus a small
     "add new SKU" dialog when nothing matches. Selection reuses fetchSKUDetails,
     so the autofill chain stays the single source of truth. */
  const [skuSearch, setSkuSearch] = useState({ index: null, q: "", results: [], open: false, loading: false, hi: -1, byName: false });
  const skuSearchTimer = useRef(null);
  const skuSearchSeq = useRef(0);
  const [newSkuDialog, setNewSkuDialog] = useState(null);
  const [savingNewSku, setSavingNewSku] = useState(false);

  // Full-catalog browser modal: blank query lists all SKUs (paged); a query runs
  // the bounded search. Opened from the SKU field's Browse icon.
  const CATALOG_PAGE_SIZE = 50;
  const [catalog, setCatalog] = useState({ open: false, index: null, q: "", items: [], total: 0, activeTotal: 0, archivedTotal: 0, page: 0, mode: "list", view: "browse", loading: false, loadingMore: false, error: false, showArchived: false });
  const [catalogSelected, setCatalogSelected] = useState(() => new Set()); // product_sku_id values checked in the modal
  const [catalogArchive, setCatalogArchive] = useState(null); // catalogue row awaiting archive confirmation
  const [archivingSku, setArchivingSku] = useState(false);
  const [restoringSku, setRestoringSku] = useState(null); // product_sku_id currently being restored
  const catalogSeq = useRef(0);
  const catalogDebounce = useRef(null);

  const runCatalogQuery = (q, includeArchived) => {
    const seq = ++catalogSeq.current;
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    const trimmed = String(q || "").trim();
    setCatalog((c) => ({ ...c, loading: true, error: false }));
    const url = trimmed ? API.searchProductSkus(merchantId, trimmed, 20, includeArchived) : API.listProductSkus(merchantId, 0, CATALOG_PAGE_SIZE, includeArchived);
    axios.get(url, { headers: { Authorization: authToken }, validateStatus: () => true })
      .then((res) => {
        if (seq !== catalogSeq.current) return; // a newer query superseded this one
        if (res.status >= 200 && res.status < 300) {
          const items = trimmed ? (Array.isArray(res.data) ? res.data : []) : (Array.isArray(res.data?.items) ? res.data.items : []);
          const total = trimmed ? items.length : (typeof res.data?.total === "number" ? res.data.total : items.length);
          setCatalog((c) => ({ ...c, items, total, page: 0, mode: trimmed ? "search" : "list", loading: false, error: false }));
        } else {
          setCatalog((c) => ({ ...c, loading: false, error: true }));
        }
      })
      .catch(() => { if (seq === catalogSeq.current) setCatalog((c) => ({ ...c, loading: false, error: true })); });
  };

  // Catalogue-wide active/archived tallies for the header, independent of the
  // current filter/search. Two tiny (size=1) count calls; refreshed on open and
  // after any archive / restore / create so the header always shows both.
  const refreshCatalogCounts = () => {
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    const headers = { Authorization: authToken };
    const totalOf = (res) => (res && res.status >= 200 && res.status < 300 && typeof res.data?.total === "number") ? res.data.total : null;
    Promise.all([
      axios.get(API.listProductSkus(merchantId, 0, 1, false), { headers, validateStatus: () => true }),
      axios.get(API.listProductSkus(merchantId, 0, 1, true), { headers, validateStatus: () => true }),
    ]).then(([activeRes, allRes]) => {
      const active = totalOf(activeRes);
      const all = totalOf(allRes);
      setCatalog((c) => ({
        ...c,
        activeTotal: active != null ? active : c.activeTotal,
        archivedTotal: (all != null && active != null) ? Math.max(0, all - active) : c.archivedTotal,
      }));
    }).catch(() => {});
  };

  const openCatalog = (index) => {
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    setCatalogSelected(new Set());
    setCatalog({ open: true, index, q: "", items: [], total: 0, activeTotal: 0, archivedTotal: 0, page: 0, mode: "list", view: "browse", loading: true, loadingMore: false, error: false, showArchived: false });
    runCatalogQuery("", false);
    refreshCatalogCounts();
  };

  const closeCatalog = () => {
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    catalogSeq.current += 1;
    setCatalogSelected(new Set());
    setNewSkuDialog(null);
    setCatalogArchive(null);
    setCatalog((c) => ({ ...c, open: false, view: "browse" }));
  };

  const onCatalogSearchChange = (q) => {
    setCatalog((c) => ({ ...c, q }));
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    catalogDebounce.current = setTimeout(() => runCatalogQuery(q, catalog.showArchived), 300);
  };

  // "Show archived" flips the server-side filter and re-runs the current query.
  const toggleShowArchived = () => {
    const next = !catalog.showArchived;
    setCatalog((c) => ({ ...c, showArchived: next }));
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    runCatalogQuery(catalog.q, next);
  };

  // Escape closes the catalogue layer by layer, wherever focus happens to be
  // (the modal's own onKeyDown only fires while focus is inside it).
  useEffect(() => {
    if (!catalog.open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (catalogArchive) { if (!archivingSku) setCatalogArchive(null); return; }
      if (catalog.view === "create") { cancelCatalogCreate(); return; }
      closeCatalog();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [catalog.open, catalog.view, catalogArchive, archivingSku]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreCatalog = () => {
    if (String(catalog.q || "").trim() || catalog.loadingMore) return; // no paging in search mode
    if (catalog.items.length >= catalog.total) return;
    const next = catalog.page + 1;
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    setCatalog((c) => ({ ...c, loadingMore: true }));
    axios.get(API.listProductSkus(merchantId, next, CATALOG_PAGE_SIZE, catalog.showArchived), { headers: { Authorization: authToken }, validateStatus: () => true })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          const more = Array.isArray(res.data?.items) ? res.data.items : [];
          const moreTotal = typeof res.data?.total === "number" ? res.data.total : null;
          setCatalog((c) => ({ ...c, items: [...c.items, ...more], page: next, total: moreTotal ?? c.total, loadingMore: false }));
        } else {
          setCatalog((c) => ({ ...c, loadingMore: false }));
        }
      })
      .catch(() => setCatalog((c) => ({ ...c, loadingMore: false })));
  };

  // "New SKU" inside the catalogue switches this modal to its create view
  // (in place) rather than opening a separate dialog.
  const addSkuFromCatalog = () => {
    const idx = catalog.index;
    const q = String(catalog.q || "").trim();
    setNewSkuDialog({ index: idx, skuId: q ? suggestSkuFromName(q) : "", name: q, price: "", hsn: "", matches: [] });
    setCatalog((c) => ({ ...c, view: "create" }));
  };

  const cancelCatalogCreate = () => {
    setNewSkuDialog(null);
    setCatalog((c) => ({ ...c, view: "browse" }));
  };

  // Edit an existing SKU from the catalogue: same form, SKU id locked (the id is
  // the upsert key — changing it would create a second SKU, not rename this one).
  const editSkuFromCatalog = (row) => {
    setNewSkuDialog({
      index: catalog.index,
      editing: true,
      skuId: row.product_sku_id || "",
      name: row.product_name || "",
      price: row.item_pricing_value?.unit_price != null ? String(row.item_pricing_value.unit_price) : "",
      hsn: row.product_hsn_code || "",
      matches: [],
    });
    setCatalog((c) => ({ ...c, view: "create" }));
  };

  // Archive = soft delete. The row stays in the catalogue (preserved for product
  // analytics + order history) but drops out of the active picker. Reversible via
  // restore. With "show archived" off it leaves the list; with it on it stays,
  // re-styled as archived.
  const STATUS_ARCHIVED = "ARCHIVED";
  const confirmArchiveSku = async () => {
    if (!catalogArchive || archivingSku) return;
    const sku = catalogArchive.product_sku_id;
    setArchivingSku(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      await axios.post(API.archiveProductSku(merchantId, sku), null, { headers: { Authorization: authToken } });
      setCatalog((c) => {
        if (c.showArchived) {
          return { ...c, items: (c.items || []).map((x) => x.product_sku_id === sku ? { ...x, status: STATUS_ARCHIVED } : x) };
        }
        const rest = (c.items || []).filter((x) => x.product_sku_id !== sku);
        return { ...c, items: rest, total: c.mode === "list" ? Math.max(0, (c.total || 0) - 1) : c.total };
      });
      setCatalogSelected((prev) => { const next = new Set(prev); next.delete(sku); return next; });
      setCatalogArchive(null);
      refreshCatalogCounts();
      toast.success("SKU archived. Restore it anytime from “Show archived”.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't archive the SKU. Please try again.");
    } finally {
      setArchivingSku(false);
    }
  };

  // Restore is the inverse of archive and low-risk (reversible), so it acts
  // immediately without a confirmation step.
  const restoreSku = async (row) => {
    const sku = row.product_sku_id;
    if (!sku || restoringSku) return;
    setRestoringSku(sku);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      await axios.post(API.unarchiveProductSku(merchantId, sku), null, { headers: { Authorization: authToken } });
      setCatalog((c) => ({ ...c, items: (c.items || []).map((x) => x.product_sku_id === sku ? { ...x, status: "ACTIVE" } : x) }));
      refreshCatalogCounts();
      toast.success("SKU restored to your catalogue.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't restore the SKU. Please try again.");
    } finally {
      setRestoringSku(null);
    }
  };

  // The Add-SKU form body, shared by the standalone dialog and the catalogue's
  // in-place create view so both stay identical.
  const renderSkuFormFields = () => (
    <>
      {!newSkuDialog?.editing && newSkuDialog?.matches && newSkuDialog.matches.length > 0 && (
        <div className="rounded-xl border border-amber-200/70 bg-amber-50 px-3 py-2.5">
          <p className="text-[11.5px] font-semibold text-amber-800">This product may already be in your catalog:</p>
          <div className="mt-1.5 space-y-1">
            {newSkuDialog.matches.map((match) => (
              <div key={match.product_sku_id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-[12px] text-amber-900" title={match.product_name || match.product_sku_id}>
                  {match.product_name || "—"} <span className="font-mono text-[10.5px] text-amber-700">{match.product_sku_id}</span>
                </span>
                <button type="button" onClick={() => { const idx = newSkuDialog.index; setNewSkuDialog(null); closeCatalog(); chooseSkuResult(idx, match); }} className="flex-shrink-0 cursor-pointer rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100">
                  Use this SKU
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className={LABEL}>SKU</label>
        <div className="relative">
          <input
            type="text"
            value={newSkuDialog?.skuId || ""}
            onChange={(e) => setNewSkuDialog((d) => ({ ...d, skuId: e.target.value }))}
            disabled={Boolean(newSkuDialog?.editing)}
            className={inputClass(false, "h-10 pr-11 font-mono text-[12.5px] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500")}
            placeholder="SKU code"
          />
          {!newSkuDialog?.editing && (
            <button
              type="button"
              onClick={() => setNewSkuDialog((d) => ({ ...d, skuId: generateRandomSku() }))}
              title="Generate a random SKU"
              aria-label="Generate a random SKU"
              className="absolute right-1 top-1 grid h-8 w-8 cursor-pointer place-items-center rounded-md text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
            >
              <SparklesIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {newSkuDialog?.editing && (
          <p className="mt-1.5 text-[11.5px] text-slate-400">The SKU code can't be changed — it identifies this product. Add a new SKU for a different code.</p>
        )}
      </div>
      <div>
        <label className={LABEL}>Product name</label>
        <input type="text" value={newSkuDialog?.name || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, name: e.target.value }))} className={inputClass(false, "h-10")} placeholder="e.g. Cotton kurta set" />
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        <div>
          <label className={LABEL}>Unit price <span className="font-medium text-slate-400">· optional · incl. GST</span></label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-slate-400">₹</span>
            <input type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()} min="0" step="0.01" value={newSkuDialog?.price || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, price: e.target.value }))} className={inputClass(false, "h-10 pl-7 text-right tabular-nums")} placeholder="e.g. 100" />
          </div>
        </div>
        <div>
          <label className={LABEL}>HSN <span className="font-medium text-slate-400">· optional</span></label>
          <input type="text" value={newSkuDialog?.hsn || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, hsn: e.target.value }))} className={inputClass(false, "h-10 font-mono text-[12.5px]")} placeholder="e.g. 610910" />
        </div>
      </div>
    </>
  );

  const toggleCatalogSelect = (sku) => {
    const id = String(sku || "").trim();
    if (!id) return;
    setCatalogSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Add each checked SKU as its own line. A SKU already in the order is skipped
  // (no duplicates) and reported. Package weight/dimensions seed from the first
  // newly-added SKU that carries them; the merchant can adjust them in the package step.
  const addSelectedFromCatalog = () => {
    const chosen = catalog.items.filter((it) => catalogSelected.has(it.product_sku_id));
    if (!chosen.length) return;
    const idx = catalog.index;
    const existing = new Set((formData.orderDetails.itemDetails || []).map((it) => String(it.skuId || "").trim()).filter(Boolean));
    const toAdd = chosen.filter((r) => !existing.has(String(r.product_sku_id || "").trim()));
    const skipped = chosen.length - toAdd.length;
    closeCatalog();
    if (toAdd.length) {
      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        const pkg = { ...prev.packageDetails };
        const present = new Set(items.map((it) => String(it.skuId || "").trim()).filter(Boolean));
        let seededDims = false;
        let filledEmpty = false;
        for (const r of toAdd) {
          const sku = String(r.product_sku_id || "").trim();
          if (!sku || present.has(sku)) continue;
          present.add(sku);
          if (!seededDims) {
            const w = r.product_product_sku_master_item_weight ?? r.product_weight ?? null;
            if (w?.product_weight != null) pkg.weight = w.product_weight;
            if (r.product_dimensions?.product_length != null) pkg.length = r.product_dimensions.product_length;
            if (r.product_dimensions?.product_width != null) pkg.breadth = r.product_dimensions.product_width;
            if (r.product_dimensions?.product_height != null) pkg.height = r.product_dimensions.product_height;
            seededDims = true;
          }
          const line = {
            name: r.product_name || "",
            skuId: sku,
            quantity: "1",
            unitPrice: r.item_pricing_value?.unit_price != null ? String(r.item_pricing_value.unit_price) : "",
            hsnCode: r.product_hsn_code || "",
            isFragile: false,
          };
          if (!filledEmpty && idx != null && items[idx] && !String(items[idx].skuId || "").trim() && !String(items[idx].name || "").trim()) {
            items[idx] = { ...items[idx], ...line };
            filledEmpty = true;
          } else {
            items.push(line);
          }
        }
        return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items }, packageDetails: pkg };
      });
      toast.success(`${toAdd.length} item${toAdd.length === 1 ? "" : "s"} added.`);
    }
    if (skipped) toast.error(`${skipped} already in your order — ${skipped === 1 ? "it was" : "they were"} skipped.`);
  };

  const openBulkSkuPage = () => {
    window.open("/tools?tab=skuMaster", "_blank", "noopener,noreferrer");
  };

  const closeSkuSearch = () => {
    if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);
    skuSearchSeq.current += 1;
    setSkuSearch((prev) => (prev.open || prev.index !== null ? { index: null, q: "", results: [], open: false, loading: false, hi: -1, byName: false } : prev));
  };

  const queueSkuSearch = (index, raw, byName = false) => {
    const q = String(raw || "").trim();
    if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);
    if (q.length < 2) { skuSearchSeq.current += 1; setSkuSearch({ index, q, results: [], open: false, loading: false, hi: -1, byName }); return; }
    skuSearchTimer.current = setTimeout(async () => {
      const seq = ++skuSearchSeq.current;
      setSkuSearch({ index, q, results: [], open: true, loading: true, hi: -1, byName });
      try {
        const authToken = localStorage.getItem("authToken");
        const merchantId = localStorage.getItem("merchantId");
        const response = await axios.get(API.searchProductSkus(merchantId, q), {
          headers: { Authorization: authToken },
          validateStatus: () => true,
        });
        if (seq !== skuSearchSeq.current) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setSkuSearch({ index, q, results: list.slice(0, 6), open: true, loading: false, hi: list.length ? 0 : -1, byName });
      } catch (err) {
        if (seq === skuSearchSeq.current) setSkuSearch({ index, q, results: [], open: true, loading: false, hi: -1, byName });
      }
    }, 300);
  };

  // A SKU uniquely identifies one product variant and may not appear on two lines.
  // Returns true if another line (or, with exceptIndex omitted, any line) already
  // carries this non-empty SKU. Empty SKUs are never treated as duplicates.
  const DUPLICATE_SKU_MSG = "This item is already in your order — duplicates aren't allowed.";
  const orderHasSku = (rawSku, exceptIndex = -1) => {
    const sku = String(rawSku || "").trim();
    if (!sku) return false;
    const items = formData.orderDetails.itemDetails || [];
    return items.some((it, j) => j !== exceptIndex && String(it.skuId || "").trim() === sku);
  };

  const chooseSkuResult = (index, result) => {
    const sku = result.product_sku_id || "";
    closeSkuSearch();
    if (sku && orderHasSku(sku, index)) { toast.error(DUPLICATE_SKU_MSG); return; }
    handleItemChange(index, "skuId", sku);
    if (sku) fetchSKUDetails(index, sku);
  };

  const openNewSkuDialog = (index) => {
    const q = skuSearch.q;
    const byName = skuSearch.byName;
    closeSkuSearch();
    const item = formData.orderDetails.itemDetails[index] || {};
    const name = byName ? q : (item.name || "");
    setNewSkuDialog({ index, skuId: byName ? suggestSkuFromName(q) : q, name, price: item.unitPrice || "", hsn: item.hsnCode || "", matches: [] });
    const dedupeQuery = String(name || item.name || "").trim();
    if (dedupeQuery.length >= 2) {
      (async () => {
        try {
          const authToken = localStorage.getItem("authToken");
          const merchantId = localStorage.getItem("merchantId");
          const response = await axios.get(API.searchProductSkus(merchantId, dedupeQuery, 4), {
            headers: { Authorization: authToken },
            validateStatus: () => true,
          });
          const list = Array.isArray(response.data) ? response.data.slice(0, 3) : [];
          if (list.length) setNewSkuDialog((d) => (d ? { ...d, matches: list } : d));
        } catch (err) { /* dedupe hints are best-effort */ }
      })();
    }
  };

  const saveNewSku = async () => {
    if (!newSkuDialog) return;
    const { index, skuId, name, price, hsn, editing } = newSkuDialog;
    if (!String(skuId || "").trim() || !String(name || "").trim()) {
      toast.error("SKU and product name are required.");
      return;
    }
    setSavingNewSku(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      const body = {
        product_sku_id: String(skuId).trim(),
        product_name: String(name).trim(),
        product_hsn_code: String(hsn || "").trim() || null,
        unit_price: price !== "" && Number.isFinite(Number(price)) ? Number(price) : null,
        currency: "INR",
      };
      const response = await axios.post(API.createProductSku(merchantId), body, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      const saved = response.data || {};
      const savedSku = saved.product_sku_id || body.product_sku_id;
      setNewSkuDialog(null);
      if (editing) {
        // Edit saves back to the catalogue only: refresh the row in place and
        // return to browse. Lines already in the order keep their snapshot.
        setCatalog((c) => ({
          ...c,
          view: "browse",
          items: (c.items || []).map((x) => x.product_sku_id === savedSku
            ? { ...x, product_name: body.product_name, product_hsn_code: body.product_hsn_code, item_pricing_value: { ...(x.item_pricing_value || {}), unit_price: body.unit_price, unit_currency: "INR" } }
            : x),
        }));
        toast.success("SKU updated.");
      } else if (catalog.open) {
        // Created from inside the catalogue: drop it into the browse list, pre-select
        // it, and return to browse so it can be added alongside other picks.
        const newRow = saved.product_sku_id ? saved : {
          product_sku_id: savedSku, product_name: body.product_name, product_hsn_code: body.product_hsn_code,
          item_pricing_value: { unit_price: body.unit_price, unit_currency: "INR" },
        };
        setCatalog((c) => {
          const rest = (c.items || []).filter((x) => x.product_sku_id !== savedSku);
          return { ...c, view: "browse", items: [newRow, ...rest], total: c.mode === "list" ? rest.length + 1 : c.total };
        });
        setCatalogSelected((prev) => new Set(prev).add(savedSku));
        refreshCatalogCounts();
        toast.success("SKU added to your catalogue.");
      } else if (orderHasSku(savedSku, index)) {
        toast.success("SKU added to your catalog.");
        toast.error(DUPLICATE_SKU_MSG);
      } else {
        setFormData((prev) => {
          const items = [...prev.orderDetails.itemDetails];
          items[index] = { ...items[index], skuId: savedSku, name: body.product_name, unitPrice: body.unit_price != null ? String(body.unit_price) : items[index].unitPrice, hsnCode: body.product_hsn_code || items[index].hsnCode };
          return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items } };
        });
        toast.success("SKU added to your catalog.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || (editing ? "Couldn't save the changes. Please try again." : "Couldn't add the SKU. Please try again."));
    } finally {
      setSavingNewSku(false);
    }
  };

  const fetchSKUDetails = async (index, sku) => {
    if (!sku) return;
    try {
      const authToken = localStorage.getItem('authToken');
      const merchantId = localStorage.getItem('merchantId');
      const skuUrl = API.getProductSku(merchantId, sku);

      const response = await axios.get(skuUrl, {
        headers: { Authorization: authToken },
        validateStatus: () => true,
      });

      const data = typeof response.data === 'object' && response.data !== null ? response.data : null;
      if (!data || Object.keys(data).length === 0) {
        toast.error('Empty product data received for SKU.');
        return;
      }

      const weightObj = data.product_product_sku_master_item_weight ?? data.product_weight ?? data.product_product_weight ?? null;

      // Safely extract fields with fallbacks to current values
      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        const currentItem = items[index] || {};

        const productName = (data.product_name ?? currentItem.name) || '';
        const unitPrice = (data.item_pricing_value?.unit_price ?? currentItem.unitPrice) || '';
        const quantity = "1"; // original logic sets to 1 on fetch
        const existingSkuId = currentItem.skuId || '';

        // Dimensions & weight (only update if present)
        const newPackageDetails = { ...prev.packageDetails };
        if (weightObj?.product_weight != null) {
          newPackageDetails.weight = weightObj.product_weight;
        }
        if (data.product_dimensions?.product_length != null) {
          newPackageDetails.length = data.product_dimensions.product_length;
        }
        if (data.product_dimensions?.product_width != null) {
          newPackageDetails.breadth = data.product_dimensions.product_width;
        }
        if (data.product_dimensions?.product_height != null) {
          newPackageDetails.height = data.product_dimensions.product_height;
        }

        const newUnits = { ...units };
        if (data.product_weight?.unit) newUnits.weight = data.product_weight.unit;
        if (data.product_dimensions?.unit) {
          newUnits.length = data.product_dimensions.unit;
          newUnits.breadth = data.product_dimensions.unit;
          newUnits.height = data.product_dimensions.unit;
        }

        items[index] = {
          ...currentItem,
          name: productName,
          unitPrice: unitPrice,
          quantity: quantity,
          skuId: existingSkuId,
        };

        return {
          ...prev,
          orderDetails: { ...prev.orderDetails, itemDetails: items },
          packageDetails: newPackageDetails,
          units: newUnits,
        };
      });
    } catch (err) {
      toast.error('Unable to fetch product details');
    }
  };

  // Conversion functions for payload only
  const convertLength = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'inch' ? num * 2.54 : num;
  };
  const convertWeight = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'kg' ? num * 1000 : num;
  };

  const handlePackageValueChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      packageDetails: { ...prev.packageDetails, [field]: value }
    }));
  };
  const handlePackageUnitChange = (field, unit) => {
    setUnits(prev => ({ ...prev, [field]: unit }));
  };

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.trim();
    setFormData(prev => ({
      ...prev,
      customerDetails: { ...prev.customerDetails, pincode: pin, city: "", state: "" }
    }));
    setPincodeLookupFailed(false);

    if (!/^\d{6}$/.test(pin)) return;

    setLoadingPincode(true);
    const db = await loadPincodeDb();
    const [entry] = db ? db.search(pin) : [];
    if (entry) {
      setFormData(prev => ({
        ...prev,
        customerDetails: {
          ...prev.customerDetails,
          city: entry.city,
          state: entry.state
        }
      }));
      setLoadingPincode(false);
      return;
    }
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      const data = response.data?.[0];
      if (data?.Status === 'Success' && data.PostOffice?.length) {
        const postOffice = data.PostOffice[0];
        setFormData(prev => ({
          ...prev,
          customerDetails: {
            ...prev.customerDetails,
            city: postOffice.District,
            state: postOffice.State
          }
        }));
      } else {
        throw new Error('No Postal data');
      }
    } catch (err) {
      setPincodeLookupFailed(true);
    } finally {
      setLoadingPincode(false);
    }
  };

  useEffect(() => {
    if (step === 1) {
      fetchAndStorePickupDetails();
    }
  }, [step]);

  useEffect(() => { loadPincodeDb(); }, []);

  // Resolve how this merchant's shipment charge settles (SQL wallet type OR Mongo onboarding preference),
  // for the preview's settlement note. Fails open: on any error we keep null and fall back to the redux
  // wallet type below, so the note works before and after the OMS endpoint is deployed.
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return undefined;
    const merchantId = localStorage.getItem("merchantId");
    let cancelled = false;
    axios
      .get(API.merchantSettlementType(), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
      .then((res) => {
        if (!cancelled && res && res.status >= 200 && res.status < 300) {
          setResolvedSettlement(res.data?.settlement_type ?? null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // After a failed Continue, put the cursor in the first field that needs attention.
  useEffect(() => {
    if (!Object.keys(errors).length) return;
    document.querySelector('[aria-invalid="true"]')?.focus();
  }, [errors]);

  // On a step change after first paint: bring the card back into view if the merchant had
  // scrolled, and put the cursor in the step's first field. Nothing moves on initial load.
  const flowRef = useRef(null);
  const settledRef = useRef(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = flowRef.current;
      if (settledRef.current && el) {
        const top = el.getBoundingClientRect().top;
        if (top < 0 || top > 200) el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
      const first = el?.querySelector('input:not([type="radio"]):not([type="search"]):not([disabled]), textarea')
        || el?.querySelector('input[type="radio"]:checked, [role="switch"]');
      first?.focus({ preventScroll: true });
    });
    settledRef.current = true;
    return () => cancelAnimationFrame(id);
  }, [step]);

  const fetchAndStorePickupDetails = async ({ skipAutoSelect = false } = {}) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      return [];
    }

    const parentClientId = localStorage.getItem("parentClientId");
    const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}/v1/pocas/onboarding/${parentClientId}/getpickupdetails`;

    try {
      const response = await axios.get(apiUrl, { headers: { Authorization: authToken } });

      if (response.data && response.data.length > 0) {
        localStorage.setItem("pickupLocations", JSON.stringify(response.data));
        setPickupDetails(response.data);
        if (!skipAutoSelect) setSelectedPickup(response.data[0]);
        return response.data;
      } else {
        setPickupDetails([]);
        return [];
      }
    } catch (error) {
      setPickupDetails([]);
      return [];
    }
  };

  useEffect(() => {
    if (step === 2) {
      const tryMatchClonePickup = (addresses) => {
        // If we have cloneFrom pickup data, try to match against saved addresses
        if (!cloneFrom) return false;
        const clonedPostal = (cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "").trim();
        const clonedPhone = (cloneFrom.pickup_poc_contact_number || "").trim();
        const clonedAddress = (cloneFrom.pickup_address || "").trim().toLowerCase();

        if (!clonedPostal && !clonedPhone && !clonedAddress) return false;

        // Try to find a matching saved address (by pincode + phone, or pincode + address substring)
        const match = addresses.find((addr) => {
          const addrPostal = (addr.pickup_postal_code || "").trim();
          const addrPhone = (addr.pickup_poc_contact_number || "").trim();
          const addrAddress = (addr.pickup_address || "").trim().toLowerCase();

          if (clonedPostal && addrPostal === clonedPostal) {
            if (clonedPhone && addrPhone === clonedPhone) return true;
            if (clonedAddress && addrAddress.includes(clonedAddress.slice(0, 30))) return true;
          }
          return false;
        });

        if (match) {
          setSelectedPickup(match);
          return true;
        }

        // No match found - auto-open "Add New Address" modal pre-filled with cloned pickup data
        const mappedPickup = {
          pickup_address: cloneFrom.pickup_address || "",
          pickup_city: cloneFrom.pickup_city || "",
          pickup_postal_code: cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "",
          pickup_poc: cloneFrom.pickup_name || cloneFrom.pickup_poc || "",
          pickup_organisation: cloneFrom.pickup_organisation || "",
          pickup_poc_contact_number: cloneFrom.pickup_poc_contact_number || "",
          pickup_poc_email: cloneFrom.pickup_poc_email || "",
          pickup_state: cloneFrom.pickup_state || "",
          pickup_district: cloneFrom.pickup_district || "",
          pickup_address_type: cloneFrom.pickup_address_type || "BUSINESS",
          pickup_country_code: cloneFrom.pickup_country_code || "IN",
          pickup_landmark: cloneFrom.pickup_landmark || "",
          gst_number: cloneFrom.pickup_gst_number || cloneFrom.gst_number || "",
        };
        // Only open modal if there's meaningful data to pre-fill
        const hasData = mappedPickup.pickup_postal_code || mappedPickup.pickup_address || mappedPickup.pickup_poc;
        if (hasData) {
          setAddrModalMode("add");
          setAddrModalLocation(mappedPickup);
          setAddrModalOpen(true);
        }
        return false;
      };

      const stored = JSON.parse(localStorage.getItem("pickupLocations") || "[]");
      if (Array.isArray(stored) && stored.length > 0) {
        setPickupDetails(stored);
        if (!tryMatchClonePickup(stored)) {
          setSelectedPickup(stored[0]);
        }
      } else {
        // Fetch with skipAutoSelect so we can try matching first
        fetchAndStorePickupDetails({ skipAutoSelect: true }).then((fetched) => {
          if (fetched && fetched.length > 0) {
            if (!tryMatchClonePickup(fetched)) {
              setSelectedPickup(fetched[0]);
            }
          } else if (cloneFrom) {
            // No saved addresses at all - open add modal with cloneFrom data
            tryMatchClonePickup([]);
          }
        });
      }
    }
  }, [step]);

  /* ── Address management handlers ── */
  const openAddAddress = () => { setAddrModalMode("add"); setAddrModalLocation(null); setAddrModalOpen(true); };
  const openEditAddress = (loc) => { setAddrModalMode("edit"); setAddrModalLocation(loc); setAddrModalOpen(true); };
  const handleAddressSaved = () => {
    fetchAndStorePickupDetails().then((fetched) => {
      // Auto-select the most recently added address (last in list)
      if (fetched && fetched.length > 0) {
        setSelectedPickup(fetched[fetched.length - 1]);
      }
    });
  };
  const openDeleteAddress = (id) => { setPickupToDelete(id); setDeleteConfirmOpen(true); };
  const confirmDeleteAddress = async () => {
    setDeletingPickup(true);
    await deletePickupAddress(pickupToDelete, () => {
      fetchAndStorePickupDetails();
      if (selectedPickup?.pickup_address_id === pickupToDelete) setSelectedPickup(null);
    });
    setDeletingPickup(false);
    setDeleteConfirmOpen(false);
    setPickupToDelete(null);
  };

  const steps = [
    { key: "customerDetails", label: "Recipient" },
    { key: "pickupDetails", label: "Pickup" },
    { key: "orderDetails", label: "Payment & items" },
    { key: "packageDetails", label: "Package" },
    { key: "courierDetails", label: "Courier" },
    { key: "summary", label: "Booked" },
  ];

  // The rules for one section, as an errors object. validateStep (the stepper's Continue) and
  // validateAll (Book in the stacked layout) both run exactly these.
  const validateSection = (currentSection) => {
    const newErrors = {};
    const addErr = (k, msg) => { newErrors[k] = msg; };

    if (currentSection === "pickupDetails" && !selectedPickup) {
      addErr("pickup", "Choose a pickup location to continue");
    }

    if (currentSection === "customerDetails") {
      const cd = formData.customerDetails || {};
      if (!String(cd.name || "").trim()) addErr("name", "Name is required");
      if (!/^[0-9]{10}$/.test(String(cd.phoneNumber || ""))) addErr("phoneNumber", "Phone Number must be 10 digits");
      const addr = String(cd.address || "").trim();
      if (!addr) addErr("address", "Address is required");
      else if (addr.length < 10) addErr("address", "Address looks too short");
      if (!String(cd.city || "").trim()) addErr("city", "City is required");
      if (!String(cd.state || "").trim()) addErr("state", "State is required");
      if (!/^[0-9]{6}$/.test(String(cd.pincode || ""))) addErr("pincode", "Pincode must be 6 digits");
      const email = String(cd.email || "").trim();
      if (email && !EMAIL_RE.test(email)) addErr("email", "Enter a valid email address");
    }

    if (currentSection === "orderDetails") {
      const od = formData.orderDetails || {};

      if (!cloneMode && !String(od.clientOrderId || "").trim()) {
        addErr("clientOrderId", "Client OrderID is required");
      } else if (!cloneMode && String(od.clientOrderId || "").trim() !== "" && orderRefCheck.ref === String(od.clientOrderId || "").trim() && orderRefCheck.outcome !== "NONE") {
        addErr("clientOrderId", "An order already exists for this reference");
      }

      // Invoice Value required and > 0
      const invoiceNum = parseFloat(od.invoiceValue || 0);
      if (!(Number.isFinite(invoiceNum) && invoiceNum > 0)) {
        addErr("invoiceValue", "Invoice Value must be greater than 0");
      }

      if (String(formData.paymentMode || "").toUpperCase() === "COD") {
        const codAmt = parseFloat(od.codAmount ?? "");
        if (!(Number.isFinite(codAmt) && codAmt >= 0)) {
          addErr("codAmount", "CodAmount is required for COD orders");
        }
      }

      // Item-level validations
      (od.itemDetails || []).forEach((item, index) => {
        if (!String(item.name || "").trim()) {
          addErr(`name-${index}`, "Name is required");
        }

        if (!cloneMode) {
          if (!String(item.skuId || "").trim()) {
            addErr(`skuId-${index}`, "SKU ID is required");
          }
        }

        if (!String(item.quantity ?? "").trim() || Number(item.quantity) <= 0 || !Number.isInteger(Number(item.quantity))) {
          addErr(`quantity-${index}`, "Quantity is required and must be > 0");
        }
        if (!String(item.unitPrice ?? "").trim() || Number(item.unitPrice) < 0) {
          addErr(`unitPrice-${index}`, "Unit Price is required");
        }
      });
    }

    if (currentSection === "packageDetails") {
      const pd = formData.packageDetails || {};

      // Weight validation
      const weightVal = String(pd.weight ?? "").trim();
      if (!weightVal) {
        addErr("weight", "Weight is required");
      } else if (isNaN(Number(weightVal)) || Number(weightVal) <= 0) {
        addErr("weight", "Weight must be a valid number greater than 0");
      } else if ((units.weight === "kg" ? Number(weightVal) : Number(weightVal) / 1000) > 200) {
        addErr("weight", "Weight looks too high - check the unit");
      }

      // Dimension validations
      const lengthVal = String(pd.length ?? "").trim();
      if (!lengthVal) {
        addErr("length", "Length is required");
      } else if (isNaN(Number(lengthVal)) || Number(lengthVal) <= 0) {
        addErr("length", "Length must be a valid number greater than 0");
      } else if ((units.length === "inch" ? Number(lengthVal) * 2.54 : Number(lengthVal)) > 500) {
        addErr("length", "Length looks too high - check the unit");
      }

      const breadthVal = String(pd.breadth ?? "").trim();
      if (!breadthVal) {
        addErr("breadth", "Breadth is required");
      } else if (isNaN(Number(breadthVal)) || Number(breadthVal) <= 0) {
        addErr("breadth", "Breadth must be a valid number greater than 0");
      } else if ((units.breadth === "inch" ? Number(breadthVal) * 2.54 : Number(breadthVal)) > 500) {
        addErr("breadth", "Breadth looks too high - check the unit");
      }

      const heightVal = String(pd.height ?? "").trim();
      if (!heightVal) {
        addErr("height", "Height is required");
      } else if (isNaN(Number(heightVal)) || Number(heightVal) <= 0) {
        addErr("height", "Height must be a valid number greater than 0");
      } else if ((units.height === "inch" ? Number(heightVal) * 2.54 : Number(heightVal)) > 500) {
        addErr("height", "Height looks too high - check the unit");
      }
    }

    return newErrors;
  };
  const validateStep = () => {
    const newErrors = validateSection(steps[step - 1].key);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Stacked layout: earlier sections stay editable, so Book re-checks every revealed section
  // and points at the first one with a problem.
  const validateAll = () => {
    const all = {};
    let firstBad = null;
    for (let n = 1; n <= 5; n++) {
      const errs = validateSection(steps[n - 1].key);
      if (Object.keys(errs).length && firstBad == null) firstBad = n;
      Object.assign(all, errs);
    }
    setErrors(all);
    return { ok: Object.keys(all).length === 0, firstBad };
  };

  const handleNext = () => {
    if (validateStep()) {
      if (step === 5) {
        handleSubmit();
      } else {
        const nextStep = step === 4 ? 5 : step + 1;
        setStep(nextStep);
        setHighestStepReached(prev => Math.max(prev, nextStep)); // Track highest step reached
        if (step === 4) {
          lastCourierKeyRef.current = courierInputsKey; // these rates belong to these inputs
          prefetchCouriersForStep5();
        }
      }
    } else {
    }
  };

  /* "↵ Enter to continue" must be true everywhere, not only where the native
     form submit reaches. This covers the gaps: steps with no inputs (pickup,
     courier), the address textarea (Shift+Enter keeps the newline), and blank
     focus. Inputs on steps 1-4 keep the native implicit submission; buttons,
     links and open dialogs keep their own Enter. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.isComposing || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (loading || savingDraft) return;
      if (addrModalOpen || deleteConfirmOpen || showCustomerModal || draftSuccessModal.open) return;
      const tag = document.activeElement?.tagName;
      if (tag === "BUTTON" || tag === "A") return;
      if (stacked) return; // stacked: Book is a deliberate click, never Enter
      if (step <= 4 && (tag === "INPUT" || tag === "SELECT")) return;
      e.preventDefault();
      handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handleBack = () => {
    setStep(prev => (prev > 1 ? prev - 1 : prev));
  };

  /* ── Stacked layout ── */
  const scrollToSection = (n) => {
    const el = document.querySelector(`[data-section="${n}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleBookStacked = () => {
    const { ok, firstBad } = validateAll();
    if (!ok) { if (firstBad) scrollToSection(firstBad); return; }
    handleSubmit();
  };
  // Switching layouts keeps everything typed. Stacked shows every section, so the whole form
  // counts as reached (the ticket fills from completed groups either way).
  const chooseLayout = (mode) => {
    if (mode === layoutMode) return;
    setLayoutMode(mode);
    writeLayoutPreference(mode);
    if (mode === "vertical") { setStep(5); setHighestStepReached(5); return; }
    // Back to Steps: the stepper's position must come from the data, not from the "5" that
    // stacked mode pins — otherwise every pill shows as reached/ticked with nothing filled.
    // Land on the first section that doesn't validate yet (or Courier if all do).
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    setErrors({});
  };
  // Stacked shows every section, so the form always sits at the last step — except once booked
  // (step 6 is the confirmation, which must stay put).
  useEffect(() => {
    if (stacked && step < 5) { setStep(5); setHighestStepReached(5); }
  }, [stacked, step]);
  // Stacked has no Continue on the Package step, so the courier rates (same two calls the stepper
  // makes on that Continue) fetch on their own once recipient, pickup and package are valid —
  // and again when any of those inputs change.
  const courierInputsKey = JSON.stringify([
    formData.customerDetails.pincode, selectedPickup?.pickup_address_id, formData.paymentMode,
    formData.orderDetails.invoiceValue, formData.orderDetails.codAmount,
    formData.packageDetails, units,
  ]);
  const courierInputsValid = stacked
    && Object.keys(validateSection("customerDetails")).length === 0
    && Object.keys(validateSection("pickupDetails")).length === 0
    && Object.keys(validateSection("packageDetails")).length === 0;
  // Stacked gating: a section unlocks only once every section above it validates (live, as the
  // merchant types). Section 1 is always open. Recomputed each render — validateSection is pure.
  const sectionValid = stacked ? steps.slice(0, 5).map((s) => Object.keys(validateSection(s.key)).length === 0) : [];
  const unlockedUpTo = stacked ? (sectionValid.findIndex((ok) => !ok) === -1 ? 5 : sectionValid.findIndex((ok) => !ok) + 1) : 5;
  const lastCourierKeyRef = useRef("");
  // The fetcher is a fresh closure every render; the latest one is kept in prefetchRef (assigned
  // right after its definition below) so this effect's deps can be exactly the inputs.
  const prefetchRef = useRef(null);
  useEffect(() => {
    if (!stacked || !courierInputsValid || courierInputsKey === lastCourierKeyRef.current) return undefined;
    const t = window.setTimeout(() => { lastCourierKeyRef.current = courierInputsKey; prefetchRef.current(); }, 500);
    return () => window.clearTimeout(t);
  }, [stacked, courierInputsValid, courierInputsKey]);

  // Step navigation via band headers / dock dots
  const handleStepClick = (targetStep) => {
    // Only allow navigation to steps that have been reached before
    if (targetStep <= highestStepReached && targetStep !== step) {
      setStep(targetStep);
    }
  };

  const handleCourierSelection = async (useRecommendation) => {
    setFormData((prev) => ({
      ...prev,
      courierDetails: {
        ...prev.courierDetails,
        useRecommendation,
        courierPartner: useRecommendation ? "" : prev.courierDetails.courierPartner,
      },
    }));
  };

  const prefetchCouriersForStep5 = async () => {
    try {
      setLoadingCouriers(true);
      await Promise.all([
        fetchCourierPricing(),
        fetchServiceability(),
      ]);
    } finally {
      setLoadingCouriers(false);
    }
  };
  prefetchRef.current = prefetchCouriersForStep5;
  // Where a freshly hydrated draft/clone should land (Steps only): the first section that
  // doesn't validate yet. Render-fresh values are read through refs so this effect depends on
  // exactly the one thing that should fire it.
  const landingRef = useRef(null);
  landingRef.current = () => {
    if (stacked) return; // stacked shows everything; its gate derives from the data anyway
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    if (landing === 5) { lastCourierKeyRef.current = courierInputsKey; prefetchCouriersForStep5(); }
  };
  useEffect(() => {
    if (!pendingDraftLanding) return;
    setPendingDraftLanding(false);
    landingRef.current();
  }, [pendingDraftLanding]);

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value.trim()
    setFormData((prev) => ({
      ...prev,
      customerDetails: { ...prev.customerDetails, phoneNumber },
    }))

    if (/^\d{10}$/.test(phoneNumber)) {
      try {
        const authToken = localStorage.getItem('authToken')
        const merchantId = localStorage.getItem('merchantId')
        const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_ADDRESS_LOOKUP_FROM_MOBILE_NUMBER}`
        const response = await axios.request({
          method: 'POST',
          url: apiUrl,
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          data: {
            phone_number: phoneNumber,
            merchant_id: merchantId,
          },
        })

        if (Array.isArray(response.data) && response.data.length > 0) {
          setCustomerLookupResults(response.data)
          setShowCustomerModal(true)
        }
      } catch (err) {
      }
    }
  }

  const selectCustomer = (cust) => {
    setFormData((prev) => ({
      ...prev,
      customerDetails: {
        ...prev.customerDetails,
        name: cust.drop_poc || '',
        phoneNumber: cust.drop_poc_contact_number || prev.customerDetails.phoneNumber,
        email: cust.drop_poc_email || prev.customerDetails.email,
        address: cust.drop_address || '',
        city: cust.drop_city || '',
        state: cust.drop_state || '',
        pincode: cust.drop_postal_code || '',
      },
    }))
    setShowCustomerModal(false)
  }

  const fetchCourierPricing = async () => {
    setLoadingCouriers(true);
    setCourierOptions([]);
    try {
      const allocatedCarriersRaw = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        toast.error("Missing authentication.");
        setLoadingCouriers(false);
        return;
      }

      const forwardCarriers = allocatedCarriersRaw
        .map((c) => (typeof c === "string" ? { carrier_partner_id: c } : c))
        .filter(
          (c) =>
            c &&
            typeof c.carrier_partner_id === "string" &&
            !reverseMatcher.test(c.carrier_partner_id)
        );

      if (!forwardCarriers.length) {
        toast.info("No forward-enabled carriers allocated.");
        setLoadingCouriers(false);
        return;
      }

      const requestPayload = forwardCarriers.map((carrier) => ({
        correlation_id: uuidv4(),
        carrier_partner_id: carrier.carrier_partner_id,
        shipment_type: "FORWARD",
        payment_mode: formData.paymentMode.toUpperCase(),
        invoice_value: parseFloat(formData.orderDetails.invoiceValue),
        rvp_with_quality_check: false,
        weight_in_grams: convertWeight(formData.packageDetails.weight, units.weight),
        length_in_cm: convertLength(formData.packageDetails.length, units.length),
        breadth_in_cm: convertLength(formData.packageDetails.breadth, units.breadth),
        height_in_cm: convertLength(formData.packageDetails.height, units.height),
        pickup_pincode: selectedPickup?.pickup_postal_code,
        drop_pincode: formData.customerDetails.pincode,
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_MERCHANT_DOMESTIC_PRICING_BATCH}`,
        requestPayload,
        {
          headers: {
            Authorization: authToken,
            "x-merchant-id": merchantId,
            "Content-Type": "application/json",
          },
        }
      );

      // Deduplicate by carrier_partner_public_name: keep first occurrence only
      const seenNames = new Set();
      const formattedOptions = (response.data || [])
        .filter(rate => !reverseMatcher.test(rate?.carrier_partner_id || ""))
        .filter((rate) => {
          const name = rate.carrier_partner_public_name;
          if (seenNames.has(name)) return false;
          seenNames.add(name);
          return true;
        })
        .map((rate) => ({
          carrier_partner_public_name: rate.carrier_partner_public_name,
          total_amount_charged_after_tax: rate.total_amount_charged_after_tax,
          max_tat_in_days: rate.max_tat_in_days,
        }));

      setCourierOptions(formattedOptions);
      // A manual pick belongs to the rate list it was made from. Once the route/package/payment
      // changes and the list is replaced, drop it (and say so) — the price it carried may no
      // longer apply. Smart allocation is unaffected.
      setFormData((prev) => {
        const picked = prev.courierDetails.courierPartner;
        if (!picked || prev.courierDetails.useRecommendation) return prev;
        toast.info("Shipment details changed — pick a courier again from the updated rates.");
        return { ...prev, courierDetails: { ...prev.courierDetails, courierPartner: "" } };
      });
    } catch (error) {
      toast.error("Failed to fetch courier rates.");
    } finally {
      setLoadingCouriers(false);
    }
  };

  const fetchServiceability = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        toast.error("Missing auth token.");
        return;
      }
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CHECK_MERCHANT_SERVICEABILITY}`;
      const payload = {
        pickup_pincode: selectedPickup?.pickup_postal_code,
        drop_pincode: formData.customerDetails.pincode,
      };
      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });
      if (
        response.data &&
        response.data.merchant_domestic_serviceabilities &&
        response.data.merchant_domestic_serviceabilities.length > 0
      ) {
        const serviceData = response.data.merchant_domestic_serviceabilities
          .filter(p => !/REVERSE/i.test(p.carrier_partner_id))
          .map(p => ({
            id: p.carrier_partner_id,
            name: (p.carrier_partner_identifier_string || "N/A").trim(),
            prepaid: !!p.prepaid_serviceability_details?.is_serviceable,
            cod: !!p.cod_serviceability_details?.is_serviceable,
          }));
        setCourierServiceability(serviceData);
      } else {
        toast.error("No serviceability data available.");
      }
    } catch (error) {
      toast.error("Failed to fetch serviceability details.");
    }
  };

  const safe = (obj, path, fallback = "") => {
    try {
      return path.split('.').reduce((a, b) => (a && a[b] !== undefined ? a[b] : undefined), obj) ?? fallback;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const cloneFrom = location?.state?.cloneFrom;
    if (!cloneFrom) return;

    const mappedItems = (cloneFrom.shipment_packages ?? [])
      .flatMap(pkg => (pkg.items ?? []).map(it => ({
        name: safe(it, 'item_general_details.item_name', ''),
        quantity: safe(it, 'quantity', 1),
        unitPrice: safe(it, 'item_payment_details.unit_price', '') || '',
        skuId: safe(it, 'item_general_details.sku', '') || '',
        hsnCode: safe(it, 'item_general_details.hsn_code', '') || '',
        isFragile: Boolean(safe(pkg, 'special_handling_details.is_fragile', false) || safe(pkg, 'special_handling_details.fragile', false)),
      })));

    const items = mappedItems.length ? mappedItems : [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "", isFragile: false }];

    const pm = (safe(cloneFrom, 'payment_details.payment_mode', '') || safe(cloneFrom, 'order_type', '')).toString().toUpperCase();
    const paymentMode = pm === 'COD' ? 'COD' : 'Prepaid';

    const firstPkg = (cloneFrom.shipment_packages && cloneFrom.shipment_packages[0]) || {};
    const packageDims = cloneFrom.shipment_package_dimensions || {};
    const weightInGrams = safe(cloneFrom, 'dead_weight_in_grams', '') || safe(firstPkg, 'weight.value', '');
    const lengthVal = safe(packageDims, 'length', '') || safe(firstPkg, 'dimensions.length', '') || '';
    const breadthVal = safe(packageDims, 'breadth', '') || safe(firstPkg, 'dimensions.breadth', '') || '';
    const heightVal = safe(packageDims, 'height', '') || safe(firstPkg, 'dimensions.height', '') || '';

    const newForm = {
      customerDetails: {
        name: safe(cloneFrom, 'drop_name', ''),
        phoneNumber: safe(cloneFrom, 'drop_poc_contact_number', ''),
        email: safe(cloneFrom, 'drop_poc_email', '') || '',
        address: safe(cloneFrom, 'drop_address', '') || '',
        city: safe(cloneFrom, 'drop_city', '') || '',
        state: safe(cloneFrom, 'drop_state', '') || '',
        pincode: safe(cloneFrom, 'drop_pincode', '') || safe(cloneFrom, 'drop_postal_code', '') || '',
      },
      orderDetails: {
        clientOrderId: uuidv4(),
        invoiceValue: String(safe(cloneFrom, 'payment_details.total_amount', '') || safe(cloneFrom, 'invoice_value', '') || ''),
        itemDetails: items,
        invoiceNumber: safe(cloneFrom, 'invoice_number', '') || '',
        codAmount: pm === 'COD' ? String(safe(cloneFrom, 'payment_details.total_amount', '') || '') : undefined,
      },
      packageDetails: {
        weight: weightInGrams ? String(weightInGrams) : "",
        length: lengthVal ? String(lengthVal) : "",
        breadth: breadthVal ? String(breadthVal) : "",
        height: heightVal ? String(heightVal) : "",
      },
      courierDetails: {
        courierPartner: safe(cloneFrom, 'allocated_carrier_partner_public_name', '') || "",
        useRecommendation: true,
      },
      paymentMode,
    };

    const inferredUnits = { weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' };
    // Prefer shipment_package_dimensions.unit, then firstPkg.dimensions.unit
    const dimensionUnit = safe(packageDims, 'unit', '') || safe(firstPkg, 'dimensions.unit', '');
    if (dimensionUnit) {
      inferredUnits.length = dimensionUnit;
      inferredUnits.breadth = dimensionUnit;
      inferredUnits.height = dimensionUnit;
    }
    const weightUnit = safe(firstPkg, 'weight.unit', '') || (cloneFrom.product_weight && cloneFrom.product_weight.unit) || '';
    if (weightUnit) inferredUnits.weight = weightUnit;

    // optionally set selectedPickup based on pickup fields in shipment
    const mappedPickup = {
      pickup_address: safe(cloneFrom, 'pickup_address', ''),
      pickup_city: safe(cloneFrom, 'pickup_city', ''),
      pickup_postal_code: safe(cloneFrom, 'pickup_pincode', '') || safe(cloneFrom, 'pickup_postal_code', ''),
      pickup_poc: safe(cloneFrom, 'pickup_name', '') || safe(cloneFrom, 'pickup_poc', ''),
      pickup_organisation: safe(cloneFrom, 'pickup_organisation', '') || '',
      pickup_poc_contact_number: safe(cloneFrom, 'pickup_poc_contact_number', ''),
      pickup_poc_email: safe(cloneFrom, 'pickup_poc_email', ''),
      pickup_state: safe(cloneFrom, 'pickup_state', ''),
      pickup_district: safe(cloneFrom, 'pickup_district', ''),
      pickup_address_type: safe(cloneFrom, 'pickup_address_type', '') || 'BUSINESS',
      pickup_country_code: safe(cloneFrom, 'pickup_country_code', '') || 'IN',
      pickup_landmark: safe(cloneFrom, 'pickup_landmark', ''),
      gst_number: safe(cloneFrom, 'pickup_gst_number', '') || safe(cloneFrom, 'gst_number', ''),
    };

    setFormData(prev => ({
      ...prev,
      ...newForm
    }));

    setUnits(inferredUnits);
    setSelectedPickup(mappedPickup);
    // A loaded draft/clone starts where its data stands, not at step 1: land on the first
    // section that doesn't validate yet, and mark the sections before it as reached — so the
    // stepper doesn't make the merchant re-walk sections the draft already filled.
    // (validateSection reads state, so this waits for the hydration above to commit.)
    setPendingDraftLanding(true);

  }, [location?.state]);

  const handleItemChange = (index, key, value) => {
    const updatedItems = [...formData.orderDetails.itemDetails];
    updatedItems[index] = { ...updatedItems[index], [key]: value };
    setFormData((prev) => ({
      ...prev,
      orderDetails: {
        ...prev.orderDetails,
        itemDetails: updatedItems,
      },
    }));
  };

  const removeItemDetail = (index) => {
    if (formData.orderDetails.itemDetails.length > 1) {
      const filteredItems = formData.orderDetails.itemDetails.filter((_, idx) => idx !== index);
      setFormData({
        ...formData,
        orderDetails: {
          ...formData.orderDetails,
          itemDetails: filteredItems
        }
      });
    } else {
      // The only item: clear it back to a blank row rather than leaving zero items.
      setFormData({
        ...formData,
        orderDetails: {
          ...formData.orderDetails,
          itemDetails: [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "", isFragile: false }]
        }
      });
      setExpandedHSNItems({});
    }
  };

  const addItemDetail = () => {
    const newItem = {
      name: "",
      quantity: 1,
      unitPrice: "",
      skuId: "",
      hsnCode: "",
      isFragile: false,
    };
    setFormData({
      ...formData,
      orderDetails: {
        ...formData.orderDetails,
        itemDetails: [...formData.orderDetails.itemDetails, newItem]
      }
    });
  };

  const buildApiPayload = (trimmedForm) => {
    // Any item flagged fragile marks the whole package for special handling.
    const anyFragile = (trimmedForm.orderDetails.itemDetails || []).some((item) => Boolean(item.isFragile));
    return {
      shipment_order_type: "FORWARD",
      process_shipment_order_in_async: false,
      pickup_details: {
        pickup_address: selectedPickup.pickup_address,
        pickup_city: selectedPickup.pickup_city,
        pickup_district: selectedPickup.pickup_district,
        pickup_state: selectedPickup.pickup_state,
        pickup_postal_code: selectedPickup.pickup_postal_code,
        pickup_poc: selectedPickup.pickup_poc,
        pickup_country_code: selectedPickup.pickup_country_code,
        pickup_poc_contact_number: selectedPickup.pickup_poc_contact_number,
        pickup_address_type: selectedPickup.pickup_address_type,
        pickup_poc_email: selectedPickup.pickup_poc_email,
        pickup_organisation: selectedPickup.pickup_organisation,
        pickup_landmark: selectedPickup.pickup_landmark,
        ...((selectedPickup.gst_number || selectedPickup.pickup_gst_number) && String(selectedPickup.gst_number || selectedPickup.pickup_gst_number).trim() && {
          gst_number: String(selectedPickup.gst_number || selectedPickup.pickup_gst_number).trim()
        }),
      },
      drop_details: {
        drop_poc: trimmedForm.customerDetails.name,
        drop_address: trimmedForm.customerDetails.address,
        drop_city: trimmedForm.customerDetails.city,
        drop_country_code: "IN",
        drop_poc_contact_number: trimmedForm.customerDetails.phoneNumber,
        drop_poc_email: trimmedForm.customerDetails.email || "abc@gmail.com",
        drop_state: trimmedForm.customerDetails.state,
        drop_postal_code: trimmedForm.customerDetails.pincode,
      },
      shipment_details: {
        ...(!trimmedForm.courierDetails.useRecommendation && trimmedForm.courierDetails.courierPartner && {
          shipment_carrier_details: {
            carrier_partner_public_name: trimmedForm.courierDetails.courierPartner,
          },
        }),
        order_details: {
          order_type: trimmedForm.paymentMode.toUpperCase(),
          order_reference_number: trimmedForm.orderDetails.clientOrderId,
          order_invoice_details: {
            invoice_value: parseFloat(trimmedForm.orderDetails.invoiceValue),
            invoice_number: trimmedForm.orderDetails.invoiceNumber || null,
            invoice_currency: "INR"
          },
          ...(trimmedForm.paymentMode.toUpperCase() === "COD" && {
            cod_amount_details: {
              value: parseFloat(trimmedForm.orderDetails.codAmount),
              currency: "INR"
            }
          })
        },
        shipment_packages: [
          {
            items: trimmedForm.orderDetails.itemDetails.map(item => {
              const itemGeneralDetails = {
                item_name: item.name,
                sku: item.skuId
              };
              if (item.hsnCode && String(item.hsnCode).trim()) {
                itemGeneralDetails.hsn_code = String(item.hsnCode).trim();
              }
              return {
                item_general_details: itemGeneralDetails,
                quantity: item.quantity,
                item_payment_details: {
                  unit_price: item.unitPrice
                }
              };
            }),
            dimensions: {
              length: convertLength(trimmedForm.packageDetails.length, units.length),
              breadth: convertLength(trimmedForm.packageDetails.breadth, units.breadth),
              height: convertLength(trimmedForm.packageDetails.height, units.height),
              unit: 'cm'
            },
            weight: {
              value: convertWeight(trimmedForm.packageDetails.weight, units.weight),
              unit: "gm"
            },
            special_handling_details: {
              fragile: anyFragile,
              is_fragile: anyFragile,
            },
          }
        ]
      }
    };
  };

  const handleSaveAsDraft = async () => {
    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const orderData = buildApiPayload(trimmedForm);

    setSavingDraft(true);
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const draftPayload = {
        order_data: orderData,
        order_source: "UI_PANEL",
        ...(trimmedForm.orderDetails.clientOrderId && {
          title: `Order ${trimmedForm.orderDetails.clientOrderId}`
        }),
      };

      const apiUrl = draftOrderId
        ? API.updateDraftOrder(draftOrderId)
        : API.createDraftOrder();

      const response = draftOrderId
        ? await axios.put(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          })
        : await axios.post(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          });

      if (response.data.metadata?.success !== false && response.data.draft_order_id) {
        const newDraftId = response.data.draft_order_id;
        // Store the draftOrderId so subsequent saves use update API (not create)
        if (!draftOrderId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else if (response.data.metadata?.success) {
        const newDraftId = draftOrderId || response.data.draft_order_id;
        if (!draftOrderId && newDraftId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else {
        toast.error(response.data.metadata?.message || "Failed to save draft.");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.metadata?.message || "Error saving draft. Please try again.";
      toast.error(errMsg);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Onboarding gate: order screens are fully usable, but placing an order requires an
    // activated account. If locked, this opens the guided onboarding overlay and preserves
    // everything the merchant entered here.
    if (!requireBusinessOps('create your order')) return;

    // Backstop for the order-reference precheck (the field-level gate already blocks Continue).
    const submitRef = String(formData.orderDetails.clientOrderId || "").trim();
    if (!cloneMode && submitRef !== "" && orderRefCheck.ref === submitRef && orderRefCheck.outcome !== "NONE") {
      toast.error("An order already exists for this reference. Use a different reference.");
      return;
    }

    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const apiPayload = buildApiPayload(trimmedForm);

    setLoading(true);
    setResponseData(null);

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CREATE_ORDER_URL}`;

      const response = await axios.post(apiUrl, apiPayload, {
        headers: {
          'Authorization': `${authToken}`,
          'Content-Type': 'application/json',
          'x-client-id': merchantId
        }
      });

      const outcome = interpretOrderCreationResponse(response.data);
      if (outcome.kind === "success") {
        setResponseData(outcome.result);
        setBookedAt(new Date());
        dispatch(fetchWalletBalance());
        new BroadcastChannel('wallet').postMessage('refresh-balance');
        setStep(6);
      } else {
        setOrderIssue(outcome.issue);
      }
    } catch (error) {
      setOrderIssue(interpretOrderCreationError(error));
    } finally {
      setLoading(false);
    }
  };

  // "Review order" lands on the section the issue points at: carrier problems on the courier
  // section, a reference clash on Payment & items, pincode / route problems and everything else
  // on the recipient section. "Stay on this order" only closes the modal.
  const reviewAfterIssue = (issue) => {
    const label = String(issue?.actionLabel || "").toLowerCase();
    const target = /carrier/.test(label) ? 5 : /reference/.test(label) ? 3 : 1;
    if (stacked) { scrollToSection(target); return; }
    setStep(target);
    setHighestStepReached((prev) => Math.max(prev, target));
    window.setTimeout(() => flowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  const handleOrderIssueAction = (action, issue) => {
    setOrderIssue(null);
    if (action === "recharge") navigate("/wallet?recharge=1");
    else if (action === "wallet") navigate("/wallet");
    else if (action === "orders") navigate("/orders");
    else if (action === "login") navigate("/login");
    else if (action === "retry") handleSubmit();
    else if (action === "review") reviewAfterIssue(issue);
  };

  /* ─────────────────────────────────────────────────────────────────────────
     Render-only derivations. Everything below reads state; nothing writes it.
  ───────────────────────────────────────────────────────────────────────── */
  const cd = formData.customerDetails;
  const od = formData.orderDetails;
  const pd = formData.packageDetails;
  const isCod = String(formData.paymentMode || "").toUpperCase() === "COD";
  /* Partial COD: the buyer paid part online, the rest is collected at the door. */
  const codAmountNum = parseFloat(od.codAmount ?? "");
  const invoiceValueNum = parseFloat(od.invoiceValue || 0);
  const isPartialCod = isCod && Number.isFinite(codAmountNum) && codAmountNum > 0 &&
    invoiceValueNum > 0 && codAmountNum < invoiceValueNum - 0.009;

  /* Validation display: a field speaks once it has been visited with content (blur) or
     after a Continue attempt - and the moment the merchant fixes it, the message and the
     red border go away. Empty required fields stay quiet until Continue, so tabbing
     through a fresh form never scolds. Render-only; validateStep still owns the gate. */
  const nonEmpty = (v) => String(v ?? "").trim() !== "";

  // Order-reference block: an existing order for the typed reference (any state but NONE).
  const orderRefForBlock = String(od.clientOrderId || "").trim();
  const orderRefBlocked = !cloneMode && orderRefForBlock !== "" && orderRefCheck.ref === orderRefForBlock && orderRefCheck.outcome !== "NONE";
  const orderRefBlockInfo = (() => {
    if (!orderRefBlocked) return null;
    const detailsHref = `/shipment-details/${encodeURIComponent(orderRefForBlock)}`;
    const unprocessedHref = `/search-unprocessed-orders?ref=${encodeURIComponent(orderRefForBlock)}`;
    switch (orderRefCheck.outcome) {
      case "PLACED": return { msg: `An order already exists for this reference${orderRefCheck.awb ? ` (AWB ${orderRefCheck.awb})` : ""}. Use a different reference for a new order.`, linkLabel: "View order", href: detailsHref };
      case "FAILED": return { msg: "This reference already has an order that didn't get placed. Use a different reference for a new order, or retry the existing one.", linkLabel: "Retry in Unprocessed Orders", href: unprocessedHref };
      case "PROCESSING": return { msg: "An order for this reference is still being processed — check back in a moment.", linkLabel: "View order", href: detailsHref };
      case "CONFIRMATION_PENDING": return { msg: "An order for this reference is awaiting customer confirmation (WhatsApp/IVR).", linkLabel: "View order", href: detailsHref };
      default: return { msg: "An order already exists for this reference. Use a different reference for a new order.", linkLabel: "View order", href: detailsHref };
    }
  })();
  // A blocked reference stops the desk at the Payment & items step (and Book in the stacked
  // layout): the buttons show it instead of refusing on click.
  const refStopsHere = orderRefBlocked && (stacked || step === 3);

  const stillBad = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-(\d+)$/.exec(key);
    if (m) {
      const it = (od.itemDetails || [])[Number(m[2])] || {};
      if (m[1] === "name") return !nonEmpty(it.name);
      if (m[1] === "skuId") return !nonEmpty(it.skuId);
      if (m[1] === "quantity") return !nonEmpty(it.quantity) || Number(it.quantity) <= 0 || !Number.isInteger(Number(it.quantity));
      return !nonEmpty(it.unitPrice) || Number(it.unitPrice) < 0;
    }
    switch (key) {
      case "phoneNumber": return !/^[0-9]{10}$/.test(String(cd.phoneNumber || ""));
      case "name": return !nonEmpty(cd.name);
      case "email": { const em = String(cd.email || "").trim(); return !!em && !EMAIL_RE.test(em); }
      case "pickup": return !selectedPickup;
      case "address": return String(cd.address || "").trim().length < 10;
      case "city": return !nonEmpty(cd.city);
      case "state": return !nonEmpty(cd.state);
      case "pincode": return !/^[0-9]{6}$/.test(String(cd.pincode || ""));
      case "clientOrderId": return !nonEmpty(od.clientOrderId) || orderRefBlocked;
      case "invoiceValue": { const n = parseFloat(od.invoiceValue || 0); return !(Number.isFinite(n) && n > 0); }
      case "codAmount": { const n = parseFloat(od.codAmount ?? ""); return !(Number.isFinite(n) && n >= 0); }
      case "weight": case "length": case "breadth": case "height": {
        const v = String(pd[key] ?? "").trim();
        if (!v || isNaN(Number(v)) || Number(v) <= 0) return true;
        if (key === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) > 200;
        return (units[key] === "inch" ? Number(v) * 2.54 : Number(v)) > 500;
      }
      default: return true;
    }
  };
  const msgFor = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-\d+$/.exec(key);
    return FIELD_MSG[m ? "item-" + m[1] : key] || "Required";
  };
  // clientOrderId's block message is link-rich, so it renders as a custom band below the field
  // (not via FieldError). Suppress the generic message here to avoid a duplicate.
  const E = (key) => {
    if (key === "clientOrderId" && orderRefBlocked) return undefined;
    return (errors[key] || touched[key]) && stillBad(key) ? msgFor(key) : undefined;
  };
  const keyStep = (key) => (/^(name|skuId|quantity|unitPrice)-\d+$/.test(key) ? 3 : FIELD_STEP[key]);
  const liveErrCount = [...new Set([...Object.keys(errors), ...Object.keys(touched)])]
    .filter((k) => keyStep(k) === step && stillBad(k)).length;
  const handleFieldBlur = (e) => {
    const key = e.target && e.target.dataset ? e.target.dataset.vkey : undefined;
    if (!key) return;
    // A number field whose text the browser can't parse reports value "" but sets
    // validity.badInput — treat it as typed-and-wrong so its error shows.
    const badNumber = e.target.type === "number" && e.target.validity && e.target.validity.badInput;
    if (!badNumber && !String(e.target.value ?? "").trim()) return;
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  /* Group-level completeness for the journey spine - the node turns emerald the moment
     a group is actually complete. Render-only; validateStep still owns the gate. */
  const emailEntered = String(cd.email || "").trim();
  const contactDone = /^[0-9]{10}$/.test(String(cd.phoneNumber || "")) && nonEmpty(cd.name) &&
    (!emailEntered || EMAIL_RE.test(emailEntered));
  const addressDone = String(cd.address || "").trim().length >= 10 && /^[0-9]{6}$/.test(String(cd.pincode || "")) && nonEmpty(cd.city) && nonEmpty(cd.state);
  const paymentDone = (() => {
    const n = parseFloat(od.invoiceValue || 0);
    if (!(Number.isFinite(n) && n > 0)) return false;
    if (isCod) { const c = parseFloat(od.codAmount ?? ""); return Number.isFinite(c) && c >= 0; }
    return true;
  })();
  const orderDone = nonEmpty(od.clientOrderId) && !orderRefBlocked;
  const itemsDone = od.itemDetails.length > 0 && od.itemDetails.every(it =>
    nonEmpty(it.name) && nonEmpty(it.skuId) &&
    nonEmpty(it.quantity) && Number(it.quantity) > 0 && Number.isInteger(Number(it.quantity)) &&
    nonEmpty(it.unitPrice) && Number(it.unitPrice) >= 0);
  const weightDone = ["weight", "length", "breadth", "height"].every(k => {
    const v = String(pd[k] ?? "").trim();
    if (!v || isNaN(Number(v)) || Number(v) <= 0) return false;
    if (k === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) <= 200;
    return (units[k] === "inch" ? Number(v) * 2.54 : Number(v)) <= 500;
  });
  const pickupDone = Boolean(selectedPickup);

  const deadKg = convertWeight(pd.weight, units.weight) / 1000;
  const volKg =
    pd.length && pd.breadth && pd.height
      ? (convertLength(pd.length, units.length) *
          convertLength(pd.breadth, units.breadth) *
          convertLength(pd.height, units.height)) / 5000
      : null;
  const billedKg = volKg != null && pd.weight ? Math.max(deadKg, volKg) : null;
  const billedBasis = billedKg == null ? null : billedKg === deadKg && deadKg >= (volKg ?? 0) ? "dead" : "vol";

  const itemsSubtotal = od.itemDetails.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const invoiceMismatch =
    itemsSubtotal > 0 && parseFloat(od.invoiceValue || 0) > 0 &&
    Math.abs(itemsSubtotal - parseFloat(od.invoiceValue)) > 0.009;

  const serviceableOf = (option) => {
    const serviceableEntries = courierServiceability.filter(
      s => s.name === (option.carrier_partner_public_name || "").trim()
    );
    return isCod ? serviceableEntries.some(s => s.cod) : serviceableEntries.some(s => s.prepaid);
  };
  const sortedOptions = [...courierOptions].sort((a, b) => {
    const sa = serviceableOf(a) ? 0 : 1;
    const sb = serviceableOf(b) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax);
  });
  const serviceableOptions = sortedOptions.filter(serviceableOf);
  const unserviceableOptions = sortedOptions.filter(o => !serviceableOf(o));
  // Ties are real: every courier at the lowest price is "Cheapest" and every one at the shortest
  // TAT is "Fastest" — the tag describes the number, not a single row.
  const finiteOf = (list, pick) => list.map(pick).filter(Number.isFinite);
  const cheapestPrices = finiteOf(serviceableOptions, (o) => Number(o.total_amount_charged_after_tax));
  // A lane without a turnaround time counts as the assumed figure it displays ("Usually 5 days") —
  // for the Fastest sort and the Fastest tag alike; on a tie, a real time leads an assumed one.
  const hasTat = (o) => Number.isFinite(Number(o.max_tat_in_days));
  const tatOf = (o) => (hasTat(o) ? Number(o.max_tat_in_days) : ASSUMED_TAT_DAYS);
  const fastestTats = serviceableOptions.map(tatOf);
  const cheapestPrice = cheapestPrices.length ? Math.min(...cheapestPrices) : null;
  const fastestTat = fastestTats.length ? Math.min(...fastestTats) : null;
  const chosenOption = courierOptions.find(
    o => o.carrier_partner_public_name === formData.courierDetails.courierPartner);
  // Sort key ('cheapest'|'fastest') drives display order only.
  const serviceableDisplay = courierSort === "fastest"
    ? [...serviceableOptions].sort((a, b) =>
        (tatOf(a) - tatOf(b)) ||
        (Number(hasTat(b)) - Number(hasTat(a))) ||
        (Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax)))
    : serviceableOptions;
  const titleCaseCity = (s) => String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const courierRoute = (selectedPickup?.pickup_city && formData.customerDetails?.city)
    ? `${titleCaseCity(selectedPickup.pickup_city)} → ${titleCaseCity(formData.customerDetails.city)}` : null;

  const filteredPickups = pickupQuery.trim()
    ? pickupDetails.filter(pk =>
        [pk.pickup_organisation, pk.pickup_poc, pk.pickup_city, pk.pickup_state, pk.pickup_postal_code, pk.pickup_address]
          .join(" ").toLowerCase().includes(pickupQuery.trim().toLowerCase()))
    : pickupDetails;
  // The chosen location leads the list — the first thing seen whenever the merchant comes back
  // to this step. The rest keep their saved order (sort is stable).
  const orderedPickups = selectedPickup
    ? [...filteredPickups].sort((a, b) => Number(selectedPickup.pickup_address_id === b.pickup_address_id) - Number(selectedPickup.pickup_address_id === a.pickup_address_id))
    : filteredPickups;

  // The truck advances one leg per completed section - it never moves on auto-selected
  // or default values, only on sections the merchant has actually finished.

  /* Callback ref: the step content mounts after AnimatePresence finishes the exit,
     later than any effect tied to the step change - so the observer attaches the
     moment the node exists and detaches when it goes. */
  const attachPickupList = (el) => {
    pickupListRef.current = el;
    if (pickupListRO.current) { pickupListRO.current.disconnect(); pickupListRO.current = null; }
    if (el) {
      const ro = new ResizeObserver(updatePickupEdges);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      pickupListRO.current = ro;
      updatePickupEdges();
    }
  };
  // Ticket progress = sections genuinely completed. Steps: the deepest step reached (Continue
  // validated everything before it). Stacked: sections that validate top-down — the same gate
  // that unlocks the next card — so an empty form is 0, never the pinned "5".
  const reached = stacked ? unlockedUpTo : highestStepReached;
  const pct = step === 6 ? 100 : Math.round(((reached - 1) / 5) * 100);
  const booked = step === 6 && responseData;
  // The form is tall and the merchant is at its bottom when they book; the confirmation is
  // short, so the browser clamps the old scroll offset to the new page's bottom and the ticket
  // opens scrolled past its stamp (phones especially). Start the confirmation from the top —
  // before paint, so there is no flash of the wrong position.
  useLayoutEffect(() => {
    if (!booked) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [booked]);

  /* ── Ticket data: strictly what the merchant has entered or reached ── */
  const dropKnown = /^[0-9]{6}$/.test(String(cd.pincode || ""));
  const originKnown = reached >= 2 && selectedPickup?.pickup_postal_code;
  const ticketRows = [
    {
      k: "Recipient",
      v: (cd.name || cd.phoneNumber) ? [cd.name, cd.phoneNumber].filter(Boolean).join(" · ") : null,
    },
    {
      k: "Pickup",
      v: originKnown ? (selectedPickup.pickup_organisation || selectedPickup.pickup_poc || selectedPickup.pickup_city) : null,
    },
    {
      k: "Courier",
      v: reached >= 5
        ? (formData.courierDetails.useRecommendation
            ? "Smart allocation"
            : chosenOption?.carrier_partner_public_name || null)
        : null,
    },
  ];
  const namedItems = od.itemDetails.filter(it => String(it.name || "").trim()).length;
  const ticketCells = [
    {
      k: reached >= 3 ? (isCod ? (isPartialCod ? "Partial COD" : "COD") : "Prepaid") : "Payment",
      v: reached >= 3
        ? (isCod && od.codAmount ? fmtInr(od.codAmount) : parseFloat(od.invoiceValue) > 0 ? fmtInr(od.invoiceValue) : null)
        : null,
    },
    { k: namedItems === 1 ? "Item" : "Items", v: namedItems ? String(namedItems) : null },
    {
      k: "Chargeable wt",
      v: billedKg != null ? `${billedKg.toFixed(2)} kg` : null,
      sub: billedKg != null && billedBasis ? (billedBasis === "vol" ? "volumetric" : "dead") : null,
    },
  ];
  const chargeValue = !formData.courierDetails.useRecommendation && chosenOption
    ? fmtInr(chosenOption.total_amount_charged_after_tax)
    : null;

  // How this shipment charge settles. Prefer the OMS-resolved type (SQL wallet OR Mongo onboarding);
  // fall back to the redux wallet type if that endpoint hasn't answered. Unknown → shown as nothing.
  const settlementLabel = (() => {
    const t = String(resolvedSettlement || walletType || "").toUpperCase();
    if (!t) return null;
    if (t.includes("COD") || t.includes("NETOFF") || t.includes("NET_OFF")) return "COD net-off";
    if (t.includes("POSTPAID") || t.includes("POST_PAID")) return "your postpaid wallet";
    if (t.includes("PREPAID") || t.includes("PRE_PAID")) return "your prepaid wallet";
    return null;
  })();

  // Booked summary: resolve the confirmed courier back to its fetched rate/TAT so the success
  // screen can show the charge and a real delivery estimate — for smart allocation (courier
  // chosen server-side) as well as manual. Degrades gracefully when a field isn't available.
  const bookedInfo = booked ? (() => {
    const name = responseData.courier_name || null;
    const opt = (name && courierOptions.find((o) => o.carrier_partner_public_name === name)) || chosenOption || null;
    const charge = opt && opt.total_amount_charged_after_tax != null ? Number(opt.total_amount_charged_after_tax) : null;
    const tat = opt && opt.max_tat_in_days != null ? Number(opt.max_tat_in_days) : null;
    const { carrier, mode } = formatCarrier(name || "");
    return { name, carrier, mode, accent: carrierAccent(carrier), charge, tat, edd: tat != null ? estDeliveryDate(tat) : null };
  })() : null;

  /* ── Step bodies (the desk side) ── */

  const renderRecipient = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={PhoneIcon} title="Contact" hint="Saved customers fill in from the phone number." done={contactDone}>
      <div className="grid gap-x-5 gap-y-3.5 md:grid-cols-2">
        <div>
          <label className={LABEL}>Phone number</label>
          <div className="relative">
            <PhoneIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              name="phoneNumber"
              placeholder="e.g. 9876543210"
              value={cd.phoneNumber}
              onChange={handlePhoneNumberChange}
              data-vkey="phoneNumber"
              aria-invalid={Boolean(E("phoneNumber"))}
              aria-describedby={E("phoneNumber") ? "err-phoneNumber" : undefined}
              className={inputClass(E("phoneNumber"), "pl-10 pr-10")}
            />
            {cd.phoneNumber && !E("phoneNumber") && cd.phoneNumber.length === 10 && (
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                {/* positioning lives on the wrapper: framer-motion owns this element's transform */}
                <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 500, damping: 24 }} className="block text-emerald-500">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} />
                </motion.span>
              </span>
            )}
          </div>
          <FieldError id="err-phoneNumber" message={E("phoneNumber")} />
        </div>
        <div>
          <label className={LABEL}>Full name</label>
          <div className="relative">
            <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="name"
              placeholder="e.g. Priya Sharma"
              value={cd.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  customerDetails: { ...prev.customerDetails, name: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, name: e.target.value.trim() } }))}
              data-vkey="name"
              aria-invalid={Boolean(E("name"))}
              aria-describedby={E("name") ? "err-name" : undefined}
              className={inputClass(E("name"), "pl-10")}
            />
          </div>
          <FieldError id="err-name" message={E("name")} />
        </div>
        <div>
          <label className={LABEL}>Email <span className="font-medium text-slate-400">· optional</span></label>
          <div className="relative">
            <MailIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              name="email"
              placeholder="e.g. priya@email.com"
              value={cd.email}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  customerDetails: { ...prev.customerDetails, email: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, email: e.target.value.trim() } }))}
              data-vkey="email"
              aria-invalid={Boolean(E("email"))}
              aria-describedby={E("email") ? "err-email" : undefined}
              className={inputClass(E("email"), "pl-10")}
            />
          </div>
          <FieldError id="err-email" message={E("email")} />
        </div>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={MapPinIcon} title="Delivery address" hint="We’ll fill the city and state from the pincode — feel free to edit or add them yourself." done={addressDone} last>
      <div>
        <label className={LABEL}>Address</label>
        <textarea
          name="address"
          placeholder="e.g. 42 Green Park, Hauz Khas"
          value={cd.address}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              customerDetails: { ...prev.customerDetails, address: e.target.value },
            }))
          }
          onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, address: e.target.value.trim() } }))}
          rows="2"
          data-vkey="address"
          aria-invalid={Boolean(E("address"))}
              aria-describedby={E("address") ? "err-address" : undefined}
          className={`${inputClass(E("address"))} h-auto min-h-[68px] resize-none py-2.5 leading-relaxed`}
        />
        <FieldError id="err-address" message={E("address")} />
      </div>
      <div className="mt-4 grid gap-x-5 gap-y-3.5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1.1fr)]">
        <div>
          <label className={LABEL}>Pincode</label>
          <div className="relative">
            <MapPinIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="pincode"
              placeholder="e.g. 400001"
              value={cd.pincode}
              onChange={handlePincodeChange}
              maxLength="6"
              data-vkey="pincode"
              aria-invalid={Boolean(E("pincode"))}
              aria-describedby={E("pincode") ? "err-pincode" : undefined}
              className={inputClass(E("pincode"), "pl-10 pr-10")}
            />
            {loadingPincode && (
              <span className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2">
                <span className="block h-full w-full animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
              </span>
            )}
          </div>
          {loadingPincode ? <p className="mt-1.5 text-[12px] font-medium text-[#3877FC]">Finding the city and state…</p> : null}
          {!loadingPincode && pincodeLookupFailed ? (
            <p className="mt-1.5 text-[12px] font-medium text-amber-700">We couldn’t find this pincode — please add the city and state below.</p>
          ) : null}
          <FieldError id="err-pincode" message={E("pincode")} />
        </div>
        <div>
          <label className={LABEL}>City</label>
          <input
            type="text"
            name="city"
            value={cd.city}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                customerDetails: { ...prev.customerDetails, city: e.target.value },
              }))
            }
            onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, city: e.target.value.trim() } }))}
            data-vkey="city"
            aria-invalid={Boolean(E("city"))}
              aria-describedby={E("city") ? "err-city" : undefined}
            className={inputClass(E("city"), cd.city ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-city" message={E("city")} />
        </div>
        <div>
          <label className={LABEL}>State</label>
          <input
            type="text"
            name="state"
            value={cd.state}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                customerDetails: { ...prev.customerDetails, state: e.target.value },
              }))
            }
            onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, state: e.target.value.trim() } }))}
            data-vkey="state"
            aria-invalid={Boolean(E("state"))}
              aria-describedby={E("state") ? "err-state" : undefined}
            className={inputClass(E("state"), cd.state ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-state" message={E("state")} />
        </div>
      </div>
      </JourneyGroup>
    </div>
  );

  // Refresh the saved locations, spinning the glyph for at least ~a full turn so the click is
  // visibly acknowledged even when the list comes back unchanged.
  const refreshPickupLocations = async () => {
    if (refreshingPickups) return;
    setRefreshingPickups(true);
    const t0 = Date.now();
    try { await Promise.resolve(fetchAndStorePickupDetails()); } catch { /* the fetch reports its own errors */ }
    window.setTimeout(() => setRefreshingPickups(false), Math.max(0, 900 - (Date.now() - t0)));
  };

  const renderPickup = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup
        icon={StoreIcon}
        title="Saved locations"
        done={pickupDone}
        last
        hint={pickupDetails.length ? `${pickupDetails.length} saved` : null}
        right={(
          <>
        {pickupDetails.length > 6 && (
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={pickupQuery}
              onChange={(e) => setPickupQuery(e.target.value)}
              placeholder="Search name, city or pincode"
              className="h-9 w-full rounded-xl border border-[#D6DEE9] bg-white pl-9 pr-3 text-[13px] sm:w-60 font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-4 focus:ring-[#3877FC]/10"
            />
          </div>
        )}
          <button
            type="button"
            onClick={refreshPickupLocations}
            title="Refresh locations"
            aria-busy={refreshingPickups}
            className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border bg-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${refreshingPickups ? "border-[#3877FC]/40 text-[#3877FC]" : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"}`}
          >
            <RefreshIcon className={`h-4 w-4 ${refreshingPickups ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={openAddAddress}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3877FC] px-3.5 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_1px_2px_rgba(15,23,42,0.10),0_6px_16px_-8px_rgba(56,119,252,0.55)] transition-all hover:bg-[#2A59BD] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 focus-visible:ring-offset-2"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={2.5} /> Add pickup location
          </button>
          </>
        )}
      >
      {E("pickup") ? (
        <p className="mb-4 rounded-lg border border-rose-200/70 bg-rose-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-700">
          Choose a pickup location to continue.
        </p>
      ) : null}
      {pickupDetails.length > 0 ? (
        filteredPickups.length > 0 ? (
          <div className="relative">
          <div ref={attachPickupList} onScroll={updatePickupEdges} className="custom-scrollbar max-h-[min(52vh,560px)] overflow-y-auto pr-1.5">
          <div className="columns-1 gap-3 md:columns-2">
            {orderedPickups.map((pickup) => {
              const isSelected = selectedPickup?.pickup_address_id === pickup.pickup_address_id;
              const locName = displayLocName(pickup.pickup_organisation || pickup.pickup_poc);
              return (
                <motion.div key={pickup.pickup_address_id} layout transition={{ type: "spring", stiffness: 380, damping: 32 }} className="group relative mb-3 break-inside-avoid">
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#3877FC]/30 ${
                      isSelected
                        ? "border-[#3877FC] bg-[#3877FC]/[0.04] shadow-[0_2px_10px_-4px_rgba(56,119,252,0.35)]"
                        : "border-slate-200 bg-white hover:border-[#AFBDD1] hover:shadow-sm"
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupLocation"
                      className="sr-only"
                      checked={isSelected}
                      onChange={() => { setSelectedPickup(pickup); pickupListRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}
                    />
                    <motion.span
                      layout
                      transition={{ type: "spring", stiffness: 500, damping: 26 }}
                      className={`mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-[12px] font-bold tracking-wide transition-colors duration-150 ${
                        isSelected ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? <CheckIcon className="h-4 w-4" strokeWidth={3} /> : locInitials(locName)}
                    </motion.span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 text-[13.5px] font-semibold leading-snug text-slate-900">{locName}</span>
                        <span className="flex flex-shrink-0 items-center gap-1">
                          <span className="font-mono text-[12px] font-semibold tabular-nums text-slate-700">{pickup.pickup_postal_code}</span>
                          <button
                            type="button"
                            aria-label={`Options for ${locName}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPickupMenuFor(pickupMenuFor === pickup.pickup_address_id ? null : pickup.pickup_address_id); }}
                            className="grid h-6 w-6 -translate-y-0.5 cursor-pointer place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                          >
                            <MoreVerticalIcon className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-snug text-slate-500">
                        {titleCaseLoc(pickup.pickup_address)}
                      </span>
                      <span className="mt-1 block text-[11.5px] leading-snug text-slate-400">
                        <span className="font-medium text-slate-500">{[titleCaseLoc(pickup.pickup_city), titleCaseLoc(pickup.pickup_state)].filter(Boolean).join(", ")}</span>
                        {pickup.pickup_poc ? ` · ${pickup.pickup_poc}` : ""}{pickup.pickup_poc_contact_number ? ` · ${pickup.pickup_poc_contact_number}` : ""}
                      </span>
                    </span>
                  </label>
                  {pickupMenuFor === pickup.pickup_address_id && (
                    <>
                      <button type="button" aria-label="Close menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setPickupMenuFor(null)} />
                      <div className="absolute right-2 top-9 z-20 w-32 overflow-hidden rounded-lg border border-[#D6DEE9] bg-white p-1 shadow-lg">
                        <button
                          type="button"
                          onClick={() => { setPickupMenuFor(null); openEditAddress(pickup); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-[#2A59BD]"
                        >
                          <PencilIcon className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPickupMenuFor(null); openDeleteAddress(pickup.pickup_address_id); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                        >
                          <TrashIcon className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
          </div>
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${pickupEdges.top ? "opacity-0" : "opacity-100"}`} />
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${pickupEdges.bottom ? "opacity-0" : "opacity-100"}`} />
          </div>
        ) : (
          <p className="text-[13.5px] text-slate-500">No saved location matches “{pickupQuery.trim()}”.</p>
        )
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-100"><StoreIcon className="h-6 w-6 text-slate-400" /></span>
          <p className="mt-1 text-[14.5px] font-semibold text-slate-800">No pickup locations yet</p>
          <p className="text-[13px] text-slate-500">Add the address the courier should collect from.</p>
          <button type="button" onClick={openAddAddress} className={`${BTN_PRIMARY} mt-3`}>
            + Add pickup location
          </button>
        </div>
      )}
      </JourneyGroup>
    </div>
  );

  const renderPayment = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={RupeeIcon} title="Payment" done={paymentDone}>
      <div className="flex flex-wrap items-start gap-x-5 gap-y-4">
        <div>
          <label className={LABEL}>Payment mode</label>
          <div className="flex h-11 items-stretch rounded-xl bg-[#EEF2F8] p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-slate-900/[0.05]">
            {["Prepaid", "COD"].map((mode) => {
              const active = formData.paymentMode === mode;
              return (
                <label key={mode} className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMode"
                    value={mode}
                    checked={active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, paymentMode: e.target.value }))}
                    className="sr-only"
                  />
                  {active ? (
                    <motion.span
                      layoutId="cfoPayThumb"
                      transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      className="absolute inset-0 rounded-lg bg-[#3877FC] shadow-[0_1px_2px_rgba(15,23,42,0.25)]"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className={`relative z-10 inline-flex h-full items-center px-5 text-[13px] font-semibold transition-[color,transform] duration-150 active:scale-[0.97] ${
                    active ? "text-white" : "text-slate-500 hover:text-slate-800"
                  }`}>
                    {mode === "COD" ? "COD" : "Prepaid"}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="w-44">
          <label className={LABEL}>Invoice value <span className="font-medium text-slate-400">· incl. GST</span></label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400">₹</span>
            <input
              type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
              value={od.invoiceValue === 0 ? "" : od.invoiceValue}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    invoiceValue: e.target.value,
                  },
                }))
              }
              data-vkey="invoiceValue"
              aria-invalid={Boolean(E("invoiceValue"))}
              aria-describedby={E("invoiceValue") ? "err-invoiceValue" : undefined}
              className={inputClass(E("invoiceValue"), "pl-8 text-right tabular-nums")}
              placeholder="e.g. 1500"
            />
          </div>
          <FieldError id="err-invoiceValue" message={E("invoiceValue")} />
        </div>
        <AnimatePresence initial={false}>
          {isCod && (
            <motion.div
              key="cod"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.16 }}
              className="w-64"
            >
              <label className={LABEL}>Amount to collect</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400">₹</span>
                <input
                  type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                  value={od.codAmount || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      orderDetails: { ...prev.orderDetails, codAmount: e.target.value },
                    }))
                  }
                  data-vkey="codAmount"
                  aria-invalid={Boolean(E("codAmount"))}
              aria-describedby={E("codAmount") ? "err-codAmount" : undefined}
                  className={inputClass(E("codAmount"), "pl-8 text-right tabular-nums")}
                  placeholder="Usually the invoice value"
                />
              </div>
              <FieldError id="err-codAmount" message={E("codAmount")} />
              {isPartialCod && !E("codAmount") ? (
                <p className="mt-1.5 flex items-start gap-1.5 text-[11.5px] font-medium text-slate-500">
                  <span className="flex-shrink-0 whitespace-nowrap rounded-md bg-[#3877FC]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2A59BD]">Partial COD</span>
                  <span className="leading-snug">{fmtInr(invoiceValueNum - codAmountNum)} paid beforehand to merchant</span>
                </p>
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={FileTextIcon} title="Order" done={orderDone}>
      {/* The reference gets the whole row: merchant references are often a UUID, and 44 mono
          characters must sit in view at any desktop width. */}
      <div className="grid gap-y-3.5">
        <div>
          <label className={LABEL}>Order reference</label>
          <div className="relative">
            <input
              type="text"
              value={od.clientOrderId}
              spellCheck={false}
              autoComplete="off"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value.trim() } }))}
              data-vkey="clientOrderId"
              aria-invalid={Boolean(E("clientOrderId")) || orderRefBlocked}
              aria-describedby={E("clientOrderId") ? "err-clientOrderId" : undefined}
              className={inputClass(Boolean(E("clientOrderId")) || orderRefBlocked, "pr-11 font-mono text-[12.5px]")}
              placeholder="e.g. SHOP-1042"
            />
            <button
              type="button"
              onClick={handleGenerateOrderId}
              title="Generate a reference"
              aria-label="Generate a reference"
              className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 cursor-pointer place-items-center rounded-lg text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
            >
              <SparklesIcon className="h-4 w-4" />
            </button>
          </div>
          <FieldError id="err-clientOrderId" message={E("clientOrderId")} />
          {orderRefBlockInfo ? (
            <div className="mt-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <p className="text-[12px] font-medium leading-snug text-rose-700">{orderRefBlockInfo.msg}</p>
              <a href={orderRefBlockInfo.href} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[11.5px] font-semibold text-[#2A59BD] transition-colors hover:text-[#1E40AF] hover:underline">
                {orderRefBlockInfo.linkLabel} <ExternalLinkIcon className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <p className="mt-1.5 text-[11.5px] text-slate-500">Your own ID for this order. Shows on the label and in reports.</p>
          )}
        </div>
        <div>
          {showShippingDetails ? (
            <div className="md:max-w-sm">
              <label className={LABEL}>Invoice number <span className="font-medium text-slate-400">· optional</span></label>
              <input
                type="text"
                value={od.invoiceNumber || ""}
                onChange={(e) =>
                  setFormData((prev2) => ({
                    ...prev2,
                    orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value },
                  }))
                }
                onBlur={(e) => setFormData((prev2) => ({ ...prev2, orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value.trim() } }))}
                className={inputClass(false)}
                placeholder="e.g. INV-2043"
              />
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setShowShippingDetails((v) => !v)}
                disabled={orderRefBlocked}
                title={orderRefBlocked ? "Sort out the order reference first" : undefined}
                className="inline-flex h-11 cursor-pointer items-center rounded-xl border border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.03] px-4 text-[13px] font-semibold text-[#2A59BD] transition-all hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.07] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#3877FC]/35 disabled:hover:bg-[#3877FC]/[0.03]"
              >
                + Add invoice number&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span>
              </button>
            </div>
          )}
        </div>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={TagIcon} title="Items" done={itemsDone} last>
      {orderRefBlocked && (
        <p className="mb-2.5 text-[12px] font-medium text-slate-500">Sort out the order reference above before adding items.</p>
      )}
      <div className={`cfo-thin-scroll overflow-x-auto ${orderRefBlocked ? "pointer-events-none select-none opacity-50" : ""}`} aria-disabled={orderRefBlocked}>
        <div className="min-w-[560px]">
          <div className="overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_1px_2px_rgba(42,89,189,0.05)]">
          {/* caps band - same grid as the rows, captions on each column's text axis */}
          <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] gap-3 border-b border-[#E7ECF3] bg-[#3877FC]/[0.06] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <span className="pl-3.5">Item</span>
            <span className="pl-3.5">SKU{cloneMode ? <span className="text-slate-500"> · optional</span> : null}</span>
            <span className="pr-3.5 text-right">Qty</span>
            <span className="pr-3.5 text-right">Unit price</span>
            <span aria-hidden="true" />
          </div>
          <div className="divide-y divide-[#E7ECF3]">
            {od.itemDetails.map((item, index) => (
              <div key={index} className="bg-[#F6F9FF] px-3 py-2.5 transition-colors hover:bg-[#EEF4FE] focus-within:bg-[#E7EFFE]">
                <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] items-start gap-3">
                  <div>
                    {/* Long names must stay fully visible: an auto-growing one-line textarea
                        in the input's material. Newlines never enter the value (Enter still
                        advances the step via the global handler; pasted breaks become spaces). */}
                    <textarea
                      rows={1}
                      value={item.name}
                      onChange={(e) => handleItemChange(index, "name", e.target.value.replace(/\s*\n\s*/g, " "))}
                      onBlur={(e) => handleItemChange(index, "name", e.target.value.trim())}
                      onKeyDown={(e) => { if (e.key === "Enter" && e.shiftKey) e.preventDefault(); }}
                      ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                      aria-label="Item name"
                      data-vkey={`name-${index}`}
                      aria-invalid={Boolean(E(`name-${index}`))}
                      aria-describedby={E(`name-${index}`) ? `err-name-${index}` : undefined}
                      className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[11px] text-sm font-medium leading-[20px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${E(`name-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                      placeholder="e.g. Cotton kurta set"
                    />
                    <FieldError id={`err-name-${index}`} message={E(`name-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <textarea
                        rows={1}
                        value={item.skuId}
                        onChange={(e) => { const v = e.target.value.replace(/\s*\n\s*/g, ""); handleItemChange(index, "skuId", v); queueSkuSearch(index, v); }}
                        onFocus={(e) => {
                          const typed = e.target.value.trim();
                          const itemName = String(item.name || "").trim();
                          if (!typed && itemName.length >= 2) queueSkuSearch(index, itemName, true);
                        }}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && orderHasSku(v, index)) { toast.error(DUPLICATE_SKU_MSG); handleItemChange(index, "skuId", ""); window.setTimeout(closeSkuSearch, 150); return; } handleItemChange(index, "skuId", v); window.setTimeout(closeSkuSearch, 150); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); return; }
                          if (!(skuSearch.open && skuSearch.index === index)) return;
                          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                            e.preventDefault();
                            const n = skuSearch.results.length;
                            if (!n) return;
                            setSkuSearch((prev) => ({ ...prev, hi: ((prev.hi + (e.key === "ArrowDown" ? 1 : -1)) + n) % n }));
                          } else if (e.key === "Enter") {
                            e.preventDefault(); e.stopPropagation();
                            if (skuSearch.hi >= 0 && skuSearch.results[skuSearch.hi]) chooseSkuResult(index, skuSearch.results[skuSearch.hi]);
                            else closeSkuSearch();
                          } else if (e.key === "Escape") {
                            e.stopPropagation();
                            closeSkuSearch();
                          }
                        }}
                        ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                        aria-label="SKU"
                        data-vkey={`skuId-${index}`}
                        aria-invalid={Boolean(E(`skuId-${index}`))}
                        aria-describedby={E(`skuId-${index}`) ? `err-skuId-${index}` : undefined}
                        className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[12px] font-mono text-[12.5px] font-medium leading-[18px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] pr-11 ${E(`skuId-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                        placeholder="e.g. SKU-1125"
                      />
                      <button
                        type="button"
                        onClick={() => generateSkuForItem(index)}
                        title="Generate a random SKU"
                        aria-label="Generate a random SKU"
                        className="absolute right-1.5 top-1.5 grid h-8 w-8 cursor-pointer place-items-center rounded-md text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                      >
                        <SparklesIcon className="h-3.5 w-3.5" />
                      </button>
                      {skuSearch.open && skuSearch.index === index && (
                        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_12px_32px_-12px_rgba(15,23,42,0.28)]">
                          {skuSearch.loading ? (
                            <div className="px-3 py-2.5 text-[12px] font-medium text-slate-500">Searching your catalog…</div>
                          ) : (
                            <>
                              {skuSearch.results.map((result, ri) => (
                                <button
                                  key={result.product_sku_id || ri}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => chooseSkuResult(index, result)}
                                  className={`flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${ri === skuSearch.hi ? "bg-[#EEF4FE]" : "hover:bg-[#F6F9FF]"}`}
                                >
                                  <span className="min-w-0">
                                    <span className="block truncate text-[12.5px] font-semibold text-slate-800">{result.product_name || "—"}</span>
                                    <span className="block font-mono text-[11px] text-slate-500">{result.product_sku_id}</span>
                                  </span>
                                  {result.item_pricing_value?.unit_price != null ? (
                                    <span className="flex-shrink-0 text-[12px] font-semibold tabular-nums text-slate-700">{fmtInr(result.item_pricing_value.unit_price)}</span>
                                  ) : null}
                                </button>
                              ))}
                              {!skuSearch.results.length && (
                                <div className="px-3 py-2.5 text-[12px] font-medium text-slate-500">No matching SKU in your catalog.</div>
                              )}
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openNewSkuDialog(index)}
                                className="flex w-full cursor-pointer items-center gap-1.5 border-t border-[#EDF1F7] bg-[#F6F9FF] px-3 py-2 text-left text-[12px] font-semibold text-[#2A59BD] transition-colors hover:bg-[#EEF4FE]"
                              >
                                {skuSearch.byName ? <>+ Add “{skuSearch.q}” to your catalog</> : <>+ Add “{skuSearch.q}” as a new SKU</>}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openCatalog(index)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2A59BD] transition-colors hover:text-[#1E44A8] focus-visible:outline-none focus-visible:underline"
                    >
                      <GridIcon className="h-3 w-3" /> Add from catalogue
                    </button>
                    <FieldError id={`err-skuId-${index}`} message={E(`skuId-${index}`)} />
                  </div>
                  <div>
                    <input
                      type="number" onKeyDown={numericKeyGuard(true)} onPaste={numericPasteGuard(true)}
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      onFocus={(e) => e.target.select()}
                      aria-label="Quantity"
                      data-vkey={`quantity-${index}`}
                      aria-invalid={Boolean(E(`quantity-${index}`))}
                        aria-describedby={E(`quantity-${index}`) ? `err-quantity-${index}` : undefined}
                      className={inputClass(E(`quantity-${index}`), "text-right tabular-nums")}
                      placeholder="e.g. 1"
                      min="1"
                    />
                    <FieldError id={`err-quantity-${index}`} message={E(`quantity-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-slate-400">₹</span>
                      <input
                        type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                        aria-label="Unit price"
                        data-vkey={`unitPrice-${index}`}
                        aria-invalid={Boolean(E(`unitPrice-${index}`))}
                        aria-describedby={E(`unitPrice-${index}`) ? `err-unitPrice-${index}` : undefined}
                        className={inputClass(E(`unitPrice-${index}`), "pl-7 text-right tabular-nums")}
                        placeholder="e.g. 100"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <FieldError id={`err-unitPrice-${index}`} message={E(`unitPrice-${index}`)} />
                  </div>
                  {/* row-level delete on its own column, clear of the HSN chip.
                      On the only remaining item it clears the row instead of removing it. */}
                  <div>
                    <button
                      type="button"
                      tabIndex={-1}
                      title={od.itemDetails.length > 1 ? "Remove this item" : "Clear this item"}
                      aria-label={od.itemDetails.length > 1 ? `Remove item ${index + 1}` : "Clear this item"}
                      onClick={() => removeItemDetail(index)}
                      className="mt-1.5 grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-600 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* meta band - one level floor: HSN + fragile left, line total on the money axis */}
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="flex flex-wrap items-center gap-2">
                    {expandedHSNItems[index] ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">HSN</span>
                        <input
                          type="text"
                          value={item.hsnCode || ""}
                          onChange={(e) => handleItemChange(index, "hsnCode", e.target.value)}
                          onBlur={(e) => handleItemChange(index, "hsnCode", e.target.value.trim())}
                          aria-label="HSN code"
                          className="h-7 w-44 rounded-lg border border-[#D6DEE9] bg-white px-2.5 font-mono text-[11.5px] font-medium text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/10"
                          placeholder="e.g. 610910"
                        />
                        <button
                          type="button"
                          aria-label="Hide HSN code"
                          title="Hide HSN code"
                          onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: false }))}
                          className="grid h-6 w-6 cursor-pointer place-items-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: !prev2[index] }))}
                        className={`inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-[10.5px] font-semibold transition-colors ${item.hsnCode ? "border-solid border-[#D6DEE9] bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-[#AFBDD1] hover:text-[#2A59BD]" : "border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.04] text-[#2A59BD] hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.08]"}`}
                      >
                        {item.hsnCode ? `HSN ${item.hsnCode}` : (<>+ HSN&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span></>)}
                      </button>
                    )}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(item.isFragile)}
                      aria-label={`Mark item ${index + 1} as fragile`}
                      title="Mark as fragile — special handling"
                      onClick={() => handleItemChange(index, "isFragile", !item.isFragile)}
                      className={`inline-flex h-6 cursor-pointer items-center gap-2 rounded-md border pl-2 pr-1.5 text-[10.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${item.isFragile ? "border-solid border-amber-300/80 bg-amber-50 text-amber-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-amber-400" : "border-dashed border-[#B9C6DA] bg-white text-slate-600 hover:border-[#AFBDD1] hover:text-slate-800"}`}
                    >
                      Fragile
                      <span
                        aria-hidden="true"
                        className={`relative h-3.5 w-6 shrink-0 rounded-full transition-colors ${item.isFragile ? "bg-amber-500" : "bg-slate-300"}`}
                      >
                        <span
                          className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow transition-[left] duration-150 ${item.isFragile ? "left-[12px]" : "left-0.5"}`}
                        />
                      </span>
                    </button>
                  </span>
                  {(Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) > 0 ? (
                    <span className="flex items-center gap-1.5 pr-3.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Line total</span>
                      <span className="flex flex-col items-end leading-tight">
                        <span className="text-[12.5px] font-semibold tabular-nums text-slate-700">{fmtInr((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}</span>
                        <span className="text-[9px] font-medium text-slate-400">(incl. GST)</span>
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {/* ghost row - the affordance shaped like what it creates */}
          <div className="border-t border-[#E7ECF3] bg-[#F6F9FF] px-3 py-2">
            <button
              type="button"
              onClick={() => {
                const nextIndex = od.itemDetails.length;
                addItemDetail();
                requestAnimationFrame(() => document.querySelector(`[data-vkey="name-${nextIndex}"]`)?.focus());
              }}
              className="flex h-9 w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-[#B9C6DA] bg-white text-[12.5px] font-semibold text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:border-[#3877FC]/50 hover:text-[#2A59BD] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
            >
              + Add another item
            </button>
          </div>
          {/* subtotal foot - always on; whispers at zero, speaks once real.
              GST note rides the total itself so it can't be missed. */}
          <div className="flex items-center justify-end gap-2 border-t border-[#E7ECF3] bg-[#3877FC]/[0.04] px-3 py-2.5 pr-[26px]">
            <span className="text-[13px] text-slate-500">Items subtotal</span>
            <span className="flex flex-col items-end leading-tight">
              <b className={`tabular-nums ${itemsSubtotal > 0 ? "text-[15.5px] font-semibold text-slate-900" : "text-[13px] font-medium text-slate-600"}`}>{fmtInr(itemsSubtotal)}</b>
              {itemsSubtotal > 0 ? <span className="text-[10px] font-medium text-slate-400">(incl. GST)</span> : null}
            </span>
          </div>
          </div>
        </div>
      </div>
      {invoiceMismatch && (
        <p className="rounded-lg border border-amber-200/70 bg-amber-50 px-3.5 py-2.5 text-[12.5px] font-medium leading-relaxed text-amber-800">
          Items add up to {fmtInr(itemsSubtotal)}, but the invoice value is {fmtInr(od.invoiceValue)}. Both are sent as entered.
        </p>
      )}
      </JourneyGroup>
    </div>
  );

  const dimFields = [
    { key: "weight", label: "Dead weight", placeholder: "e.g. 500", options: ["gm", "kg"] },
    { key: "length", label: "Length", placeholder: "e.g. 30", options: ["cm", "inch"] },
    { key: "breadth", label: "Breadth", placeholder: "e.g. 20", options: ["cm", "inch"] },
    { key: "height", label: "Height", placeholder: "e.g. 10", options: ["cm", "inch"] },
  ];

  const renderPackage = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={BoxIcon} title="Weight and size" hint="Packed weight on the scale, outer dimensions of the box." done={weightDone} last>
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-4">
        {dimFields.map(({ key, label, placeholder, options }) => (
          <div key={key}>
            <label className={LABEL}>{label}</label>
            <div className={`flex h-11 items-stretch overflow-hidden rounded-xl border bg-white transition-[border-color,box-shadow] duration-150 focus-within:border-[#3877FC] focus-within:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${
              E(key) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"
            }`}>
              <input
                type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                placeholder={placeholder}
                value={pd[key]}
                onChange={(e) => handlePackageValueChange(key, e.target.value)}
                aria-label={label}
                data-vkey={key}
                aria-invalid={Boolean(E(key))}
                  aria-describedby={E(key) ? `err-${key}` : undefined}
                className="min-w-0 flex-1 bg-transparent px-3.5 text-[14px] font-semibold tabular-nums text-slate-900 placeholder:font-medium placeholder:text-slate-500 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <select
                value={units[key]}
                onChange={(e) => handlePackageUnitChange(key, e.target.value)}
                aria-label={`${label} unit`}
                className="cursor-pointer border-l border-slate-200/80 bg-transparent px-2.5 text-[12px] font-medium text-slate-500 focus:outline-none"
              >
                {options.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <FieldError id={`err-${key}`} message={E(key)} />
          </div>
        ))}
      </div>

      {/* The studio unfolds: its height eases open (and closed) so the card grows smoothly
          instead of jumping to size — the same in the stepper and the stacked layout. */}
      <AnimatePresence initial={false}>
      {(pd.weight || pd.length || pd.breadth || pd.height) && (
        <motion.div
          key="package-studio"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ height: { type: "spring", stiffness: 150, damping: 26, mass: 1 }, opacity: { duration: 0.34, ease: "easeOut" } }}
          className="overflow-clip"
        >
          <PackageStudio
            lCm={convertLength(pd.length, units.length)}
            bCm={convertLength(pd.breadth, units.breadth)}
            hCm={convertLength(pd.height, units.height)}
            l={Number(pd.length) || 0} b={Number(pd.breadth) || 0} h={Number(pd.height) || 0}
            unitL={units.length} unitB={units.breadth} unitH={units.height}
            weightDisp={Number(pd.weight) || 0} unitW={units.weight}
            deadKg={deadKg} volKg={volKg} billedBasis={billedBasis} billedKg={billedKg}
          />
        </motion.div>
      )}
      </AnimatePresence>
      </JourneyGroup>
    </div>
  );

  const renderCourier = () => (
    <div className="cfo-stagger space-y-4">
      <SmartAllocationEngine
        on={formData.courierDetails.useRecommendation}
        onToggle={() => handleCourierSelection(!formData.courierDetails.useRecommendation)}
      />

      <AnimatePresence initial={false}>
        {!formData.courierDetails.useRecommendation && (
          <motion.div
            key="chooser"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ height: SPRING, opacity: { duration: 0.16 } }}
            className="overflow-clip"
          >
            <div className="pt-4">
              <CourierPicker
                loading={loadingCouriers}
                sortedOptions={sortedOptions}
                serviceableOptions={serviceableOptions}
                serviceableDisplay={serviceableDisplay}
                unserviceableOptions={unserviceableOptions}
                showUnserviceable={showUnserviceable}
                onToggleUnserviceable={() => setShowUnserviceable(v => !v)}
                courierSort={courierSort}
                onSortChange={setCourierSort}
                courierRoute={courierRoute}
                cheapestPrice={cheapestPrice}
                fastestTat={fastestTat}
                selected={formData.courierDetails.courierPartner}
                onSelect={(name) => setFormData((prev) => ({ ...prev, courierDetails: { ...prev.courierDetails, courierPartner: name } }))}
                serviceableOf={serviceableOf}
                paymentLabel={isCod ? "COD" : "prepaid"}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const STEP_META = [
    { key: "customerDetails", title: "Recipient", sub: "Who is this going to?", body: renderRecipient },
    { key: "pickupDetails", title: "Pickup", sub: "Where does it leave from?", body: renderPickup },
    { key: "orderDetails", title: "Payment & items", sub: "How is it paid and what's inside?", body: renderPayment },
    { key: "packageDetails", title: "Package", sub: "How big and heavy is the package?", body: renderPackage },
    { key: "courierDetails", title: "Courier", sub: "Who should carry it?", body: renderCourier },
  ];
  // Forward reads as travel, Back as return - the slide direction follows the journey.
  const prevStepRef = useRef(step);
  const stepDir = step >= prevStepRef.current ? 1 : -1;
  useEffect(() => { prevStepRef.current = step; }, [step]);
  const courierDone = formData.courierDetails.useRecommendation || Boolean(formData.courierDetails.courierPartner);
  const STEP_GROUPS = [
    [contactDone, addressDone],
    [pickupDone],
    [paymentDone, orderDone, itemsDone],
    [weightDone],
    [courierDone],
  ];
  const groupsOfStep = STEP_GROUPS[step - 1] || [];
  const groupsDone = groupsOfStep.filter(Boolean).length;
  const stepReady = groupsOfStep.length > 0 && groupsDone === groupsOfStep.length;
  const RING_C = 2 * Math.PI * 13;
  const FormBody = step <= 4 ? "form" : "div";
  // One header for a section, used by both layouts: icon, title, subtitle, and the live ring.
  const SECTION_ICON = { customerDetails: UserIcon, pickupDetails: StoreIcon, orderDetails: RupeeIcon, packageDetails: BoxIcon, courierDetails: TruckIcon };
  const renderSectionHeader = (n) => {
    const sm = STEP_META[n - 1];
    const Icon = SECTION_ICON[sm.key] || UserIcon;
    const groups = STEP_GROUPS[n - 1] || [];
    const done = groups.filter(Boolean).length;
    const complete = groups.length > 0 && done === groups.length;
    const title = !stacked && n < highestStepReached ? `Editing · ${sm.title}` : sm.title;
    const showRing = !(stacked && n > unlockedUpTo);
    return (
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_15px_-3px_rgba(56,119,252,0.25),0_4px_6px_-4px_rgba(56,119,252,0.25)]">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-900">{title}</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">{sm.sub}</p>
        </div>
        {/* the section's own pulse: groups completed, live */}
        {showRing && <span className="relative ml-auto grid h-[38px] w-[38px] flex-shrink-0 place-items-center" title={`${done} of ${groups.length} complete`}>
          <svg width="38" height="38" viewBox="0 0 38 38" className="-rotate-90">
            <circle cx="19" cy="19" r="13" fill="none" stroke="#E2E8F0" strokeWidth="3.5" />
            <motion.circle
              cx="19" cy="19" r="13" fill="none"
              stroke={complete ? "#10B981" : "#3877FC"}
              strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={RING_C}
              initial={false}
              animate={{ strokeDashoffset: RING_C * (1 - (groups.length ? done / groups.length : 0)) }}
              transition={{ type: "spring", stiffness: 90, damping: 20, delay: 0.14 }}
            />
          </svg>
          <span className={`absolute flex items-baseline overflow-hidden text-[10px] font-semibold tabular-nums ${complete ? "text-emerald-600" : "text-slate-500"}`}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={done} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -8, opacity: 0 }} transition={{ duration: 0.2, ease: "easeOut" }}>
                {done}
              </motion.span>
            </AnimatePresence>
            /{groups.length}
          </span>
        </span>}
      </div>
    );
  };

  /* ── The page ── */
  return (
    <MotionConfig reducedMotion="user">
      <div
        className="relative min-h-full bg-[#F6F8FB]"
        style={{
          backgroundImage: "radial-gradient(1100px 440px at 55% -160px, rgba(56,119,252,0.06), transparent 70%), radial-gradient(900px 420px at 6% 110%, rgba(56,119,252,0.035), transparent 70%)",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      >
        {/* cartographic dot grid, top band only - designed air, never inside a card */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(30,58,109,0.05) 1px, transparent 1.4px)",
            backgroundSize: "22px 22px",
            maskImage: "linear-gradient(to bottom, black, transparent 400px)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent 400px)",
          }}
        />
        <div className="relative mx-auto w-full max-w-[1440px] px-5 pb-4 pt-3 lg:px-8 antialiased">
          {booked ? (
            /* ───────────── Booked: the celebration takes the desk ───────────── */
            <BookedSuccess
              bookedAt={bookedAt}
              origin={{ city: selectedPickup?.pickup_city, pin: selectedPickup?.pickup_postal_code }}
              destination={{ city: cd.city, pin: cd.pincode }}
              courier={bookedInfo}
              charge={bookedInfo?.charge ?? null}
              settlement={settlementLabel}
              billedKg={billedKg}
              billedBasis={billedBasis}
              order={{
                recipientName: cd.name,
                recipientPhone: cd.phoneNumber,
                paymentLabel: isCod ? (isPartialCod ? "Partial COD" : "COD") : "Prepaid",
                paymentValue: isCod && od.codAmount ? fmtInr(od.codAmount) : (parseFloat(od.invoiceValue) > 0 ? fmtInr(od.invoiceValue) : null),
                itemsCount: namedItems,
                orderRef: responseData.order_reference_number,
                waybill: responseData.waybill,
              }}
              labelUrl={responseData.label_url}
              invoiceUrl={responseData.commercial_invoice_url}
              awbCopied={awbCopied}
              onCopyAwb={() => {
                navigator.clipboard?.writeText(String(responseData.waybill));
                setAwbCopied(true);
                window.setTimeout(() => setAwbCopied(false), 1500);
              }}
              onViewDetails={() => responseData.order_reference_number && navigate(`/shipment-details/${encodeURIComponent(responseData.order_reference_number)}`)}
              onAllShipments={() => navigate("/all-shipments")}
              onBookAnother={resetForm}
            />
          ) : (
            <div className="gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
              {/* ── The desk: quiet, fast form ── */}
              {/* Keyed by layout mode: switching Steps ⇄ Stacked remounts the desk, so the
                  layout animation never tweens between the two very different heights
                  (that read as the whole form stretching). Within a mode, `layout` still
                  eases step-to-step height changes. */}
              <motion.div
                key={layoutMode}
                ref={flowRef}
                layout
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`${stacked ? "" : CARD} scroll-mt-[76px]`}
                onBlurCapture={handleFieldBlur}
              >
                {/* Slim section pills — every section named upfront (stepper only) */}
                {!stacked && (
                <div ref={stepRowRef} className="cfo-thin-scroll sticky top-16 z-30 flex w-full items-center justify-between gap-1 overflow-x-auto rounded-t-2xl border-b border-slate-100 bg-white/95 px-5 py-2 backdrop-blur lg:px-7" aria-label="Sections">
                  {STEP_META.map((sm, index) => {
                    const n = index + 1;
                    const isActive = n === step;
                    const done = !isActive && n <= highestStepReached;
                    const reachable = n <= highestStepReached;
                    return (
                      <React.Fragment key={sm.key}>
                      <button
                        type="button"
                        data-step={n}
                        onClick={() => handleStepClick(n)}
                        aria-disabled={!reachable}
                        aria-current={isActive ? "step" : undefined}
                        className={`flex flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 ${
                          isActive
                            ? "bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_2px_8px_-2px_rgba(56,119,252,0.45)]"
                            : done
                              ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                              : "text-slate-500"
                        } ${reachable && !isActive ? "" : "cursor-default"}`}
                      >
                        {done ? (
                          <motion.span
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30, delay: 0.09 }}
                            className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white"
                          >
                            <CheckIcon className="h-2.5 w-2.5" strokeWidth={4} />
                          </motion.span>
                        ) : (
                          <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] font-semibold ${
                            isActive ? "bg-white/20 text-white" : "border border-slate-400/70 text-slate-600"
                          }`}>
                            {n}
                          </span>
                        )}
                        {sm.title}
                      </button>
                      {n < STEP_META.length && (
                        <span
                          aria-hidden="true"
                          className={`h-px min-w-[10px] flex-1 rounded-full transition-colors duration-300 ${
                            n < highestStepReached ? "bg-emerald-300" : "bg-slate-200"
                          }`}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
                )}

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center" role="status" aria-busy="true">
                    <div className="relative h-12 w-12">
                      <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
                      <div className="absolute inset-0 flex items-center justify-center text-[#3877FC]">
                        <PackageIcon className="h-4 w-4" />
                      </div>
                    </div>
                    <p className="mt-4 text-[15.5px] font-semibold text-slate-900">Booking your shipment…</p>
                    <p className="mt-1 text-[13px] text-slate-500">Allocating the courier and generating the AWB.</p>
                  </div>
                ) : (
                  <FormBody
                    noValidate={step <= 4 ? true : undefined}
                    onSubmit={step <= 4 ? (e) => { e.preventDefault(); handleNext(); } : undefined}
                  >
                    <div ref={stepBodyRef} className={stacked ? "" : "px-5 pb-5 pt-3 lg:px-7"}>
                      {stacked ? (
                        /* Stacked layout: every section is its own card, threaded on a numbered rail:
                           (01) — card — | — (02) — card — … */
                        <div>
                          {STEP_META.map((sm, i) => {
                            const n = i + 1;
                            const groups = STEP_GROUPS[i] || [];
                            const complete = groups.length > 0 && groups.every(Boolean);
                            const unlocked = n <= unlockedUpTo;
                            const current = n === unlockedUpTo;
                            return (
                              <React.Fragment key={sm.key}>
                                {/* rail: connector from the previous card, then the section number */}
                                <div className="flex flex-col items-center" aria-hidden="true">
                                  {n > 1 && <span className={`h-7 w-px ${n <= unlockedUpTo ? "bg-emerald-300" : "bg-[#D8DFEA]"}`} />}
                                  <span className={`my-2 grid h-8 min-w-[2rem] place-items-center rounded-full px-2 font-mono text-[12px] font-bold tabular-nums ring-4 ring-[#F6F8FB] ${
                                    complete && unlocked ? "bg-emerald-500 text-white"
                                      : current ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]"
                                      : unlocked ? "bg-white text-slate-600 shadow-[inset_0_0_0_1.5px_#C9D3E0]"
                                      : "bg-white text-slate-400 shadow-[inset_0_0_0_1.5px_#E2E8F0]"
                                  }`}>
                                    {complete && unlocked ? <CheckIcon className="h-4 w-4" strokeWidth={3.5} /> : String(n).padStart(2, "0")}
                                  </span>
                                </div>
                                <section
                                  data-section={n}
                                  data-locked={unlocked ? undefined : "true"}
                                  ref={(el) => { if (el) { if (unlocked) el.removeAttribute("inert"); else el.setAttribute("inert", ""); } }}
                                  className={`${CARD} relative scroll-mt-[88px] px-5 pb-5 pt-4 transition-opacity duration-300 lg:px-7 ${unlocked ? "" : "pointer-events-none select-none opacity-55"}`}
                                >
                                  {!unlocked && (
                                    <span className="absolute right-5 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F1] bg-[#F6F8FB] px-2.5 py-1 text-[11px] font-semibold text-slate-500 lg:right-7">
                                      <LockIcon className="h-3 w-3" /> Complete {STEP_META[unlockedUpTo - 1].title} first
                                    </span>
                                  )}
                                  {renderSectionHeader(n)}
                                  {sm.body()}
                                </section>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      ) : (
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key={step}
                            initial={{ opacity: 0, x: stepDir * 32 }}
                            animate={{ opacity: 1, x: 0, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1], delay: 0.07 } }}
                            exit={{ opacity: 0, x: stepDir * -24, transition: { duration: 0.16, ease: [0.4, 0, 1, 1] } }}
                          >
                            {renderSectionHeader(step)}
                            {STEP_META[step - 1].body()}
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </div>

                    <div ref={footerRef} className={stacked
                      ? `${CARD} mt-7 px-5 py-3 lg:px-7`
                      : "sticky bottom-0 rounded-b-[inherit] border-t border-[#EDF1F7] bg-[#FAFBFD]/95 px-5 py-3 backdrop-blur lg:px-7"}>
                      <AnimatePresence>
                        {moreBelow && !stacked && (
                          <motion.button
                            type="button"
                            onClick={scrollForMore}
                            aria-label="Scroll down for more"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.2 }}
                            className="absolute -top-[42px] left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-[#E2EAF8] bg-white/95 py-1.5 pl-3 pr-2.5 text-[11px] font-semibold text-[#3877FC] shadow-[0_6px_18px_-6px_rgba(15,23,42,0.28)] backdrop-blur transition-colors hover:bg-white"
                          >
                            More below
                            <motion.span animate={{ y: [0, 2.5, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>
                              <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={2.75} />
                            </motion.span>
                          </motion.button>
                        )}
                      </AnimatePresence>
                      <div className="flex flex-wrap items-center gap-3">
                        {step > 1 && !stacked && (
                          <button type="button" onClick={handleBack} className={BTN_GHOST}>
                            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Back
                          </button>
                        )}
                        {liveErrCount > 0 ? (
                          <span className="text-[12.5px] font-semibold text-rose-600" role="alert">
                            {liveErrCount} field{liveErrCount === 1 ? "" : "s"} need{liveErrCount === 1 ? "s" : ""} attention
                          </span>
                        ) : stacked ? (
                          <span className="hidden text-[12px] text-slate-500 md:inline">{unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} to unlock the next section` : "Every section is open — book when ready"}</span>
                        ) : refStopsHere ? (
                          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rose-600" role="status">
                            <LockIcon className="h-3.5 w-3.5" /> Change the order reference to continue.
                          </span>
                        ) : step <= 4 ? (
                          <span className="hidden items-center gap-1.5 text-[12px] text-slate-500 md:flex">
                            <kbd className="rounded-md border-b-2 border-[#C9D3E0] bg-white px-1.5 py-0.5 font-sans text-[11px] font-semibold text-slate-500 ring-1 ring-[#D8DFEA]">↵ Enter</kbd>
                            to continue
                          </span>
                        ) : null}
                        {/* Phones: this group takes the full width and Book/Continue stretches; desktop: right-aligned */}
                        <div className="order-first flex w-full flex-shrink-0 items-center gap-2.5 sm:order-none sm:ml-auto sm:w-auto">
                          {/* Saving a draft doesn't depend on where you are: always on the last step,
                              and on every step while editing an existing draft (Steps mode lands a
                              loaded draft on its first incomplete section). */}
                          {(step === 5 || Boolean(draftOrderId)) && (
                            <button
                              type="button"
                              onClick={handleSaveAsDraft}
                              disabled={loading || savingDraft}
                              className={BTN_OUTLINE}
                            >
                              {savingDraft && <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />}
                              {savingDraft ? "Saving…" : draftOrderId ? "Update draft" : "Save as draft"}
                            </button>
                          )}
                          {step === 5 ? (
                            <button type="button" onClick={stacked ? handleBookStacked : handleSubmit} disabled={loading || savingDraft || refStopsHere || (stacked && unlockedUpTo < 5)} title={refStopsHere ? "Change the order reference to continue" : stacked && unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} first` : undefined} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady && !refStopsHere ? "cfo-cta-ready" : ""}`}>
                              Book shipment <CheckIcon className="h-4 w-4" strokeWidth={3} />
                            </button>
                          ) : (
                            <button type={step <= 4 ? "submit" : "button"} onClick={step <= 4 ? undefined : handleNext} disabled={refStopsHere} title={refStopsHere ? "Change the order reference to continue" : undefined} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady && !refStopsHere ? "cfo-cta-ready" : ""}`}>
                              Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </FormBody>
                )}
              </motion.div>

              {/* ── The ticket: the shipment, becoming real ── */}
              <motion.aside
                initial={{ x: 16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
                className="mt-6 lg:sticky lg:top-20 lg:mt-0"
              >
                <ShipmentTicket
                  pct={pct}
                  step={step}
                  stageLabel={stacked ? `${Math.min(reached - 1, 5)} of 5 done` : undefined}
                  origin={originKnown ? { city: selectedPickup.pickup_city, pin: selectedPickup.pickup_postal_code } : null}
                  destination={dropKnown ? { city: cd.city, pin: cd.pincode } : null}
                  rows={ticketRows}
                  cells={ticketCells}
                  orderRef={String(od.clientOrderId || "").trim() || null}
                  charge={chargeValue}
                  settlement={settlementLabel}
                />
                {/* Form layout preference — how the merchant likes to fill the form; remembered for a year */}
                <FormLayoutCard layoutMode={layoutMode} onChoose={chooseLayout} />
              </motion.aside>
            </div>
          )}

          {/* SKU catalogue browser: browse/search every SKU, multi-select, and add as items */}
          {catalog.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" onClick={closeCatalog}>
              <div
                className="relative flex h-[88vh] max-h-[880px] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Escape") { if (catalogArchive) { if (!archivingSku) setCatalogArchive(null); } else { closeCatalog(); } } e.stopPropagation(); }}
              >
                {catalog.view === "create" ? (
                  <>
                    {/* Create view — add a new SKU without leaving the catalogue */}
                    <div className="flex items-center gap-3 border-b border-slate-100 px-6 pb-4 pt-5">
                      <button type="button" onClick={cancelCatalogCreate} aria-label="Back to catalogue" className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <div className="flex-1">
                        <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">{newSkuDialog?.editing ? "Edit SKU" : "Add a new SKU"}</h3>
                        <p className="mt-0.5 text-[12.5px] text-slate-500">{newSkuDialog?.editing ? "Changes save to your catalogue for future orders." : "It saves to your catalogue and gets selected here."}</p>
                      </div>
                      <button type="button" onClick={closeCatalog} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="custom-scrollbar flex-1 space-y-3.5 overflow-y-auto px-6 py-5">
                      {renderSkuFormFields()}
                    </div>
                    <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#F6F9FF] px-6 py-3.5">
                      <button type="button" onClick={cancelCatalogCreate} disabled={savingNewSku} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9] disabled:opacity-60">Back</button>
                      <button type="button" onClick={saveNewSku} disabled={savingNewSku} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:opacity-60">
                        {savingNewSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                        {savingNewSku ? (newSkuDialog?.editing ? "Saving…" : "Adding…") : (newSkuDialog?.editing ? "Save changes" : "Add SKU")}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 pb-4 pt-5">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#3877FC] text-white shadow-[0_6px_16px_-6px_rgba(56,119,252,0.6)]">
                      <GridIcon className="h-[18px] w-[18px]" />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">Your product catalogue</h3>
                      <p className="mt-0.5 text-[12.5px] text-slate-500">Tick the products you want, then add them to your order.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(catalog.activeTotal + catalog.archivedTotal) > 0 && (
                      <span className="rounded-full bg-[#3877FC]/[0.08] px-2.5 py-1 text-[11px] font-bold tabular-nums text-[#2A59BD]">
                        {catalog.activeTotal} active · {catalog.archivedTotal} archived
                      </span>
                    )}
                    <button type="button" onClick={closeCatalog} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Search band */}
                <div className="border-b border-slate-100 bg-[#F6F9FF] px-6 py-4">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      autoFocus
                      value={catalog.q}
                      onChange={(e) => onCatalogSearchChange(e.target.value)}
                      placeholder="Search by product name or SKU…"
                      className="w-full rounded-xl border border-[#D6DEE9] bg-white py-3 pl-11 pr-3 text-[13.5px] font-medium text-slate-900 shadow-sm outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 hover:border-[#AFBDD1] focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)]"
                    />
                  </div>
                  <div className="mt-2.5 flex items-center justify-end">
                    <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[12px] font-medium text-slate-500 transition-colors hover:text-slate-700">
                      <input type="checkbox" checked={catalog.showArchived} onChange={toggleShowArchived} className="h-[15px] w-[15px] cursor-pointer rounded-[4px] border-[#C7D2E4] text-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/30 focus:ring-offset-0" />
                      Show archived
                    </label>
                  </div>
                </div>

                {/* List */}
                <div
                  className="custom-scrollbar min-h-[240px] flex-1 overflow-y-auto px-4 py-3"
                  onScroll={(e) => { const el = e.currentTarget; if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMoreCatalog(); }}
                >
                  {catalog.loading ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
                      <RefreshIcon className="h-6 w-6 animate-spin text-[#3877FC]" />
                      <span className="text-[13px] font-medium">Loading your catalogue…</span>
                    </div>
                  ) : catalog.error ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                      <div className="grid h-12 w-12 place-items-center rounded-full bg-rose-50 text-rose-400"><PackageOpenIcon className="h-6 w-6" /></div>
                      <p className="text-[13px] font-medium text-slate-600">Couldn't load your catalogue.</p>
                      <button type="button" onClick={() => runCatalogQuery(catalog.q, catalog.showArchived)} className="rounded-lg bg-[#3877FC] px-4 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2A59BD]">Retry</button>
                    </div>
                  ) : catalog.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F6F9FF] text-slate-300 ring-1 ring-inset ring-[#E2EAF8]"><PackageOpenIcon className="h-7 w-7" /></div>
                      <p className="text-[13.5px] font-semibold text-slate-700">{String(catalog.q || "").trim() ? "No products match your search" : "Your catalogue is empty"}</p>
                      <p className="max-w-xs text-[12px] text-slate-500">{String(catalog.q || "").trim() ? "Try a different name or SKU, or add it as a new product." : "Add your first product to reuse it across orders."}</p>
                      <button type="button" onClick={addSkuFromCatalog} className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2A59BD] active:scale-[0.98]"><PlusIcon className="h-3.5 w-3.5" /> Add new SKU</button>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {catalog.items.map((it, i) => {
                        const archived = String(it.status || "").toUpperCase() === STATUS_ARCHIVED;
                        const added = !archived && orderHasSku(it.product_sku_id);
                        const selected = !archived && catalogSelected.has(it.product_sku_id);
                        const restoring = restoringSku === it.product_sku_id;
                        // Active rows are a <label> so a click anywhere toggles the checkbox.
                        // Archived rows have no checkbox — a <label> would forward the click to
                        // its first control (the Edit button), so use a plain <div> instead.
                        const RowTag = archived ? "div" : "label";
                        return (
                          <li key={(it.product_sku_id || "") + i}>
                            <RowTag title={archived ? "Archived — restore it first to add it to an order" : undefined} className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${archived ? "cursor-default border-transparent bg-slate-50/60" : added ? "cursor-default border-transparent opacity-60" : selected ? "cursor-pointer border-[#3877FC] bg-[#EEF4FE]" : "cursor-pointer border-transparent hover:border-[#E2EAF8] hover:bg-[#F6F9FF]"}`}>
                              {archived ? (
                                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center text-slate-300"><ArchiveIcon className="h-[15px] w-[15px]" /></span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={added || selected}
                                  disabled={added}
                                  onChange={() => toggleCatalogSelect(it.product_sku_id)}
                                  className="h-[18px] w-[18px] shrink-0 cursor-pointer rounded-[5px] border-[#C7D2E4] text-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/30 focus:ring-offset-0 disabled:cursor-default"
                                />
                              )}
                              <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg transition-colors ${archived ? "bg-slate-100 text-slate-400" : selected && !added ? "bg-[#3877FC]/[0.14] text-[#2A59BD]" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"}`}>
                                <BoxIcon className="h-[18px] w-[18px]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className={`block truncate text-[13.5px] font-semibold ${archived ? "text-slate-500" : "text-slate-800"}`}>{it.product_name || "—"}</span>
                                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-slate-600">{it.product_sku_id}</span>
                                  {it.product_hsn_code ? <span className="text-[10.5px] font-medium text-slate-400">HSN {it.product_hsn_code}</span> : null}
                                </span>
                              </div>
                              {archived ? (
                                <span className="flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-slate-500 ring-1 ring-inset ring-slate-200">Archived</span>
                              ) : added ? (
                                <span className="flex-shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold text-emerald-600 ring-1 ring-inset ring-emerald-200">Added</span>
                              ) : it.item_pricing_value?.unit_price != null ? (
                                <span className="flex flex-shrink-0 flex-col items-end leading-tight">
                                  <span className="text-[14px] font-bold tabular-nums text-slate-800">{fmtInr(it.item_pricing_value.unit_price)}</span>
                                  <span className="text-[9.5px] font-medium text-slate-400">(incl. GST)</span>
                                </span>
                              ) : null}
                              <span className="flex flex-shrink-0 items-center gap-0.5">
                                <button
                                  type="button"
                                  title="Edit SKU"
                                  aria-label={`Edit ${it.product_sku_id}`}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); editSkuFromCatalog(it); }}
                                  className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-[#3877FC]/[0.08] hover:text-[#2A59BD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                                >
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </button>
                                {archived ? (
                                  <button
                                    type="button"
                                    title="Restore SKU"
                                    aria-label={`Restore ${it.product_sku_id}`}
                                    disabled={restoring}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); restoreSku(it); }}
                                    className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40 disabled:opacity-50"
                                  >
                                    {restoring ? <RefreshIcon className="h-3.5 w-3.5 animate-spin" /> : <RestoreIcon className="h-3.5 w-3.5" />}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    title="Archive SKU"
                                    aria-label={`Archive ${it.product_sku_id}`}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCatalogArchive(it); }}
                                    className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
                                  >
                                    <ArchiveIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </span>
                            </RowTag>
                          </li>
                        );
                      })}
                      {catalog.loadingMore && <li className="flex items-center justify-center gap-2 py-4 text-[12px] text-slate-400"><RefreshIcon className="h-3.5 w-3.5 animate-spin" /> Loading more…</li>}
                    </ul>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-[#F6F9FF] px-6 py-3.5">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={addSkuFromCatalog} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      <PlusIcon className="h-3.5 w-3.5" /> New SKU
                    </button>
                    <button type="button" onClick={openBulkSkuPage} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      <ExternalLinkIcon className="h-3.5 w-3.5" /> Bulk upload
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    {catalogSelected.size > 0 && <span className="text-[12px] font-medium text-slate-500">{catalogSelected.size} selected</span>}
                    <button type="button" onClick={closeCatalog} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={addSelectedFromCatalog}
                      disabled={catalogSelected.size === 0}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                    >
                      {catalogSelected.size > 0 ? `Add ${catalogSelected.size} item${catalogSelected.size === 1 ? "" : "s"}` : "Add items"}
                    </button>
                  </div>
                </div>
                  </>
                )}
                {catalogArchive && (
                  <div className="absolute inset-0 z-20 grid place-items-center bg-slate-900/25 p-6 backdrop-blur-[2px] animate-fade-in" onClick={() => !archivingSku && setCatalogArchive(null)}>
                    <div className="w-full max-w-sm rounded-2xl border border-[#E4E9F1] bg-white p-5 shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-start gap-3">
                        <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-600"><ArchiveIcon className="h-[18px] w-[18px]" /></div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-[14.5px] font-semibold text-slate-900">Archive this SKU?</h4>
                          <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px]">
                            <span className="min-w-0 truncate font-semibold text-slate-700">{catalogArchive.product_name || "—"}</span>
                            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-slate-600">{catalogArchive.product_sku_id}</span>
                          </p>
                          <p className="mt-2 text-[12px] leading-relaxed text-slate-500">It leaves your active catalogue but stays on record — restore it anytime from “Show archived”. Items already in this order aren't affected.</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-end gap-2.5">
                        <button type="button" disabled={archivingSku} onClick={() => setCatalogArchive(null)} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9] disabled:opacity-60">Cancel</button>
                        <button type="button" disabled={archivingSku} onClick={confirmArchiveSku} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amber-500 px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(245,158,11,0.5)] transition-all hover:bg-amber-600 active:translate-y-px disabled:opacity-60">
                          {archivingSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                          {archivingSku ? "Archiving…" : "Archive SKU"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

          {/* Address management dialogs (shared components, unchanged) */}
          {newSkuDialog && !catalog.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" onClick={() => !savingNewSku && setNewSkuDialog(null)}>
              <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Escape") setNewSkuDialog(null); e.stopPropagation(); }}>
                <div className="border-b border-slate-100 px-5 pb-4 pt-5">
                  <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">Add SKU to your catalog</h3>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">Next time it fills in from a search.</p>
                </div>
                <div className="space-y-3.5 px-5 py-4">
                  {renderSkuFormFields()}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#F6F9FF] px-5 py-3.5">
                  <button type="button" onClick={() => setNewSkuDialog(null)} disabled={savingNewSku} className="inline-flex h-10 cursor-pointer items-center rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all hover:bg-[#F8FAFD] hover:text-slate-900 disabled:opacity-60">Cancel</button>
                  <button type="button" onClick={saveNewSku} disabled={savingNewSku} className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-xl bg-[#3877FC] px-4 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:opacity-60">
                    {savingNewSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    {savingNewSku ? "Adding…" : "Add SKU"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
          <PickupAddressModal open={addrModalOpen} mode={addrModalMode} location={addrModalLocation} onClose={() => setAddrModalOpen(false)} onSaved={handleAddressSaved} />
          <DeleteConfirmModal open={deleteConfirmOpen} onConfirm={confirmDeleteAddress} onCancel={() => { setDeleteConfirmOpen(false); setPickupToDelete(null); }} deleting={deletingPickup} />

          {/* Saved-customer dialog */}
          {showCustomerModal && ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cfo-saved-customer-title"
              tabIndex={-1}
              onKeyDown={(e) => { if (e.key === "Escape") setShowCustomerModal(false); }}
            >
              <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 pb-4 pt-5">
                  <div>
                    <h3 id="cfo-saved-customer-title" className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900">Saved customer</h3>
                    <p className="mt-0.5 text-[13px] text-slate-500">Choose an address to use it, or close to enter a new one.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerModal(false)}
                    aria-label="Close"
                    className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/50"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-[60vh] space-y-2.5 overflow-y-auto p-5">
                  {customerLookupResults.map((cust, i) => (
                    <button
                      key={i}
                      type="button"
                      autoFocus={i === 0}
                      onClick={() => selectCustomer(cust)}
                      className="group flex w-full items-start gap-3.5 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-[#3877FC] hover:bg-[#3877FC]/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      <span className={`mt-0.5 grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-slate-100 text-[13px] font-semibold text-slate-500 transition-colors group-hover:bg-[#3877FC]/10 group-hover:text-[#2A59BD]`}>
                        {initialsOf(cust.drop_poc)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-slate-900">{cust.drop_poc}</span>
                        <span className="mt-0.5 block text-[12.5px] leading-relaxed text-slate-500 line-clamp-2">{cust.drop_address}</span>
                        <span className="mt-0.5 block text-[12.5px] font-semibold text-slate-700">
                          {[cust.drop_city, cust.drop_state].filter(Boolean).join(", ")} {cust.drop_postal_code}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Draft saved dialog */}
          {draftSuccessModal.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" role="dialog" aria-modal="true">
              <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl animate-scale-in">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
                  <CheckIcon className="h-7 w-7" strokeWidth={2.75} />
                </div>
                <h3 className="mt-4 text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                  {draftSuccessModal.isUpdate ? "Draft updated" : "Draft saved"}
                </h3>
                {!draftSuccessModal.isUpdate && (
                  <p className="mt-1 text-[13px] text-slate-500">Find it under Draft Orders whenever you're ready.</p>
                )}
                {draftSuccessModal.draftId && (
                  <p className="mx-auto mt-2.5 w-fit rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-[11px] text-slate-500">{draftSuccessModal.draftId}</p>
                )}
                <div className="mt-5 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate("/draft-orders")}
                    className={`${BTN_PRIMARY} w-full justify-center`}
                  >
                    Open Draft Orders
                  </button>
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => navigate("/orders")}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      Orders
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDraftSuccessModal({ open: false, draftId: null, isUpdate: false });
                        navigate("/create-forward-order", { replace: true, state: {} });
                        window.location.reload();
                      }}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      New order
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          <OrderCreationIssueModal
            issue={orderIssue}
            onClose={() => setOrderIssue(null)}
            onAction={handleOrderIssueAction}
          />
        </div>
      </div>
    </MotionConfig>
  );
}

export default CreateForwardOrder;
